import type {
  AmazonCategoryRule,
} from './amazon';

export const DEFAULT_BUDGET_CATEGORIES = [
  'Rent / mortgage',
  'Council tax',
  'Service charge / ground rent',
  'Gas / electric',
  'Water',
  'Broadband',
  'Mobile',
  'Food & housekeeping',
  'Fuel',
  'Public transport',
  'Parking / tolls',
  'Vehicle tax / MOT / servicing',
  'Childcare & school',
  'Insurance',
  'Health & medical',
  'Debt repayments',
  'Subscriptions',
  'Clothing & personal care',
  'Pets',
  'Work / self-employed',
  'Savings',
  'Leisure / entertainment',
  'Other essential',
  'Other discretionary',
] as const;

export type CanonicalBudgetCategory = (typeof DEFAULT_BUDGET_CATEGORIES)[number];
export const LEGACY_BUDGET_CATEGORIES = [
  'Childcare',
  'Food & groceries',
  'School costs',
  'Utilities',
  'Transport',
  'Other',
] as const;
export type LegacyBudgetCategory = (typeof LEGACY_BUDGET_CATEGORIES)[number];
export type MoneyConfidence = 'high' | 'medium' | 'low';
export type MoneyRuleScope = 'transaction' | 'merchant' | 'pattern';
export type MoneyBreakdownSummaryGroup = 'essential' | 'debt' | 'savings' | 'discretionary';
export type MoneyBreakdownGroupKey =
  | 'housing'
  | 'utilities'
  | 'food-housekeeping'
  | 'travel'
  | 'childcare-school'
  | 'insurance'
  | 'health-medical'
  | 'debt'
  | 'subscriptions'
  | 'clothing-personal-care'
  | 'pets'
  | 'work-self-employed'
  | 'savings'
  | 'leisure-entertainment'
  | 'other-essential'
  | 'other-discretionary';
export type MoneyTransactionKind =
  | 'salary'
  | 'benefits'
  | 'maintenance'
  | 'regular-incoming-transfer'
  | 'internal-transfer'
  | 'savings-transfer'
  | 'credit-card-repayment'
  | 'refund'
  | 'cash-withdrawal'
  | 'loan-drawdown'
  | 'bank-fee'
  | 'bank-interest'
  | 'debt-repayment'
  | 'income'
  | 'spend'
  | 'needs-review';

export const DEFAULT_EXPENSE_CATEGORY: CanonicalBudgetCategory = 'Food & housekeeping';

export const MONEY_BREAKDOWN_GROUPS: Array<{
  key: MoneyBreakdownGroupKey;
  label: string;
  summaryGroup: MoneyBreakdownSummaryGroup;
}> = [
  { key: 'housing', label: 'Housing', summaryGroup: 'essential' },
  { key: 'utilities', label: 'Utilities', summaryGroup: 'essential' },
  { key: 'food-housekeeping', label: 'Food & housekeeping', summaryGroup: 'essential' },
  { key: 'travel', label: 'Travel', summaryGroup: 'essential' },
  { key: 'childcare-school', label: 'Childcare & school', summaryGroup: 'essential' },
  { key: 'insurance', label: 'Insurance', summaryGroup: 'essential' },
  { key: 'health-medical', label: 'Health & medical', summaryGroup: 'essential' },
  { key: 'debt', label: 'Debt', summaryGroup: 'debt' },
  { key: 'subscriptions', label: 'Subscriptions', summaryGroup: 'discretionary' },
  { key: 'clothing-personal-care', label: 'Clothing & personal care', summaryGroup: 'discretionary' },
  { key: 'pets', label: 'Pets', summaryGroup: 'essential' },
  { key: 'work-self-employed', label: 'Work / self-employed', summaryGroup: 'essential' },
  { key: 'savings', label: 'Savings', summaryGroup: 'savings' },
  { key: 'leisure-entertainment', label: 'Leisure / entertainment', summaryGroup: 'discretionary' },
  { key: 'other-essential', label: 'Other essential', summaryGroup: 'essential' },
  { key: 'other-discretionary', label: 'Other discretionary', summaryGroup: 'discretionary' },
] as const;

const MONEY_CATEGORY_DETAILS: Array<{
  category: CanonicalBudgetCategory;
  group: MoneyBreakdownGroupKey;
  summaryGroup: MoneyBreakdownSummaryGroup;
  benchmarkBucket?: 'utilities' | 'food' | 'transport' | 'childcare';
}> = [
  { category: 'Rent / mortgage', group: 'housing', summaryGroup: 'essential' },
  { category: 'Council tax', group: 'housing', summaryGroup: 'essential' },
  { category: 'Service charge / ground rent', group: 'housing', summaryGroup: 'essential' },
  { category: 'Gas / electric', group: 'utilities', summaryGroup: 'essential', benchmarkBucket: 'utilities' },
  { category: 'Water', group: 'utilities', summaryGroup: 'essential', benchmarkBucket: 'utilities' },
  { category: 'Broadband', group: 'utilities', summaryGroup: 'essential', benchmarkBucket: 'utilities' },
  { category: 'Mobile', group: 'utilities', summaryGroup: 'essential', benchmarkBucket: 'utilities' },
  { category: 'Food & housekeeping', group: 'food-housekeeping', summaryGroup: 'essential', benchmarkBucket: 'food' },
  { category: 'Fuel', group: 'travel', summaryGroup: 'essential', benchmarkBucket: 'transport' },
  { category: 'Public transport', group: 'travel', summaryGroup: 'essential', benchmarkBucket: 'transport' },
  { category: 'Parking / tolls', group: 'travel', summaryGroup: 'essential', benchmarkBucket: 'transport' },
  { category: 'Vehicle tax / MOT / servicing', group: 'travel', summaryGroup: 'essential', benchmarkBucket: 'transport' },
  { category: 'Childcare & school', group: 'childcare-school', summaryGroup: 'essential', benchmarkBucket: 'childcare' },
  { category: 'Insurance', group: 'insurance', summaryGroup: 'essential' },
  { category: 'Health & medical', group: 'health-medical', summaryGroup: 'essential' },
  { category: 'Debt repayments', group: 'debt', summaryGroup: 'debt' },
  { category: 'Subscriptions', group: 'subscriptions', summaryGroup: 'discretionary' },
  { category: 'Clothing & personal care', group: 'clothing-personal-care', summaryGroup: 'discretionary' },
  { category: 'Pets', group: 'pets', summaryGroup: 'essential' },
  { category: 'Work / self-employed', group: 'work-self-employed', summaryGroup: 'essential' },
  { category: 'Savings', group: 'savings', summaryGroup: 'savings' },
  { category: 'Leisure / entertainment', group: 'leisure-entertainment', summaryGroup: 'discretionary' },
  { category: 'Other essential', group: 'other-essential', summaryGroup: 'essential' },
  { category: 'Other discretionary', group: 'other-discretionary', summaryGroup: 'discretionary' },
] as const;

const MONEY_CATEGORY_DETAIL_BY_LABEL = new Map(
  MONEY_CATEGORY_DETAILS.map((entry) => [entry.category, entry]),
);

export type BankBudgetSection =
  | 'income'
  | 'housing'
  | 'utilities'
  | 'food-housekeeping'
  | 'travel'
  | 'childcare-school'
  | 'insurance'
  | 'health-medical'
  | 'debt'
  | 'subscriptions'
  | 'clothing-personal-care'
  | 'pets'
  | 'work-self-employed'
  | 'savings'
  | 'leisure-entertainment'
  | 'other-essential'
  | 'other-discretionary';

export type BankBudgetSummaryBucket = 'income' | 'essential' | 'debt' | 'savings' | 'discretionary';

export const BANK_BREAKDOWN_CATEGORY_DEFINITIONS = [
  { id: 'income_salary', section: 'income', parentLabel: 'Income', label: 'Salary', summaryBucket: 'income', budgetCategory: null },
  { id: 'income_benefits', section: 'income', parentLabel: 'Income', label: 'Benefits', summaryBucket: 'income', budgetCategory: null },
  { id: 'income_maintenance', section: 'income', parentLabel: 'Income', label: 'Maintenance', summaryBucket: 'income', budgetCategory: null },
  { id: 'income_other', section: 'income', parentLabel: 'Income', label: 'Other income', summaryBucket: 'income', budgetCategory: null },
  { id: 'housing_rent_mortgage', section: 'housing', parentLabel: 'Housing', label: 'Rent / mortgage', summaryBucket: 'essential', budgetCategory: 'Rent / mortgage' },
  { id: 'housing_council_tax', section: 'housing', parentLabel: 'Housing', label: 'Council tax', summaryBucket: 'essential', budgetCategory: 'Council tax' },
  { id: 'housing_service_charge_ground_rent', section: 'housing', parentLabel: 'Housing', label: 'Service charge / ground rent', summaryBucket: 'essential', budgetCategory: 'Service charge / ground rent' },
  { id: 'utilities_gas_electric', section: 'utilities', parentLabel: 'Utilities', label: 'Gas / electric', summaryBucket: 'essential', budgetCategory: 'Gas / electric' },
  { id: 'utilities_water', section: 'utilities', parentLabel: 'Utilities', label: 'Water', summaryBucket: 'essential', budgetCategory: 'Water' },
  { id: 'utilities_broadband', section: 'utilities', parentLabel: 'Utilities', label: 'Broadband', summaryBucket: 'essential', budgetCategory: 'Broadband' },
  { id: 'utilities_mobile', section: 'utilities', parentLabel: 'Utilities', label: 'Mobile', summaryBucket: 'essential', budgetCategory: 'Mobile' },
  { id: 'utilities_other', section: 'utilities', parentLabel: 'Utilities', label: 'Other utilities', summaryBucket: 'essential', budgetCategory: 'Other essential' },
  { id: 'food_housekeeping', section: 'food-housekeeping', parentLabel: 'Food & housekeeping', label: 'Food & housekeeping', summaryBucket: 'essential', budgetCategory: 'Food & housekeeping' },
  { id: 'travel_fuel', section: 'travel', parentLabel: 'Travel', label: 'Fuel', summaryBucket: 'essential', budgetCategory: 'Fuel' },
  { id: 'travel_public_transport', section: 'travel', parentLabel: 'Travel', label: 'Public transport', summaryBucket: 'essential', budgetCategory: 'Public transport' },
  { id: 'travel_parking_tolls', section: 'travel', parentLabel: 'Travel', label: 'Parking / tolls', summaryBucket: 'essential', budgetCategory: 'Parking / tolls' },
  { id: 'travel_vehicle_costs', section: 'travel', parentLabel: 'Travel', label: 'Vehicle tax / MOT / servicing', summaryBucket: 'essential', budgetCategory: 'Vehicle tax / MOT / servicing' },
  { id: 'travel_other', section: 'travel', parentLabel: 'Travel', label: 'Other travel', summaryBucket: 'essential', budgetCategory: 'Other essential' },
  { id: 'childcare', section: 'childcare-school', parentLabel: 'Childcare & school', label: 'Childcare', summaryBucket: 'essential', budgetCategory: 'Childcare & school' },
  { id: 'school_costs', section: 'childcare-school', parentLabel: 'Childcare & school', label: 'School costs', summaryBucket: 'essential', budgetCategory: 'Childcare & school' },
  { id: 'insurance', section: 'insurance', parentLabel: 'Insurance', label: 'Insurance', summaryBucket: 'essential', budgetCategory: 'Insurance' },
  { id: 'health_medical', section: 'health-medical', parentLabel: 'Health & medical', label: 'Health & medical', summaryBucket: 'essential', budgetCategory: 'Health & medical' },
  { id: 'debt_repayments', section: 'debt', parentLabel: 'Debt', label: 'Debt repayments', summaryBucket: 'debt', budgetCategory: 'Debt repayments' },
  { id: 'subscriptions', section: 'subscriptions', parentLabel: 'Subscriptions', label: 'Subscriptions', summaryBucket: 'discretionary', budgetCategory: 'Subscriptions' },
  { id: 'clothing_personal_care', section: 'clothing-personal-care', parentLabel: 'Clothing & personal care', label: 'Clothing & personal care', summaryBucket: 'essential', budgetCategory: 'Clothing & personal care' },
  { id: 'pets', section: 'pets', parentLabel: 'Pets', label: 'Pets', summaryBucket: 'essential', budgetCategory: 'Pets' },
  { id: 'work_self_employed', section: 'work-self-employed', parentLabel: 'Work / self-employed', label: 'Work / self-employed', summaryBucket: 'essential', budgetCategory: 'Work / self-employed' },
  { id: 'savings', section: 'savings', parentLabel: 'Savings', label: 'Savings', summaryBucket: 'savings', budgetCategory: 'Savings' },
  { id: 'leisure_entertainment', section: 'leisure-entertainment', parentLabel: 'Leisure / entertainment', label: 'Leisure / entertainment', summaryBucket: 'discretionary', budgetCategory: 'Leisure / entertainment' },
  { id: 'other_essential', section: 'other-essential', parentLabel: 'Other essential', label: 'Other essential', summaryBucket: 'essential', budgetCategory: 'Other essential' },
  { id: 'other_discretionary', section: 'other-discretionary', parentLabel: 'Other discretionary', label: 'Other discretionary', summaryBucket: 'discretionary', budgetCategory: 'Other discretionary' },
] as const satisfies ReadonlyArray<{
  id: string;
  section: BankBudgetSection;
  parentLabel: string;
  label: string;
  summaryBucket: BankBudgetSummaryBucket;
  budgetCategory: CanonicalBudgetCategory | null;
}>;

export type BankBudgetCategoryId = (typeof BANK_BREAKDOWN_CATEGORY_DEFINITIONS)[number]['id'];

export type BankCategoryRuleScope = 'transaction' | 'merchant' | 'pattern';

export type BankCategoryRule = {
  id?: string | null;
  scope: BankCategoryRuleScope;
  transactionId?: string | null;
  merchantKey?: string | null;
  merchantLabel?: string | null;
  patternKey?: string | null;
  categoryId: BankBudgetCategoryId;
};

export type BankNormalisationKind =
  | 'salary'
  | 'benefits'
  | 'maintenance'
  | 'income'
  | 'incoming-transfer'
  | 'internal-transfer'
  | 'savings-transfer'
  | 'credit-card-repayment'
  | 'refund'
  | 'cash-withdrawal'
  | 'loan-drawdown'
  | 'bank-fee'
  | 'bank-interest'
  | 'spend';

export type BankRecurringCadence = 'weekly' | 'four-weekly' | 'monthly' | 'quarterly' | 'annual';

export type BankBudgetReviewReason =
  | 'broad-merchant'
  | 'low-confidence'
  | 'cash-withdrawal'
  | 'unclear-transfer'
  | 'unclear-income';

export type BankBudgetReviewItem = {
  transactionId: string | null;
  bookingDate: string;
  amount: number;
  currency: string;
  merchantKey: string;
  merchantLabel: string;
  patternKey: string;
  rawLabel: string;
  suggestedCategoryId: BankBudgetCategoryId | null;
  suggestedCategoryLabel: string | null;
  confidence: MoneyConfidence;
  reason: BankBudgetReviewReason;
};

export type BankBudgetCategoryRow = {
  categoryId: BankBudgetCategoryId;
  categoryLabel: string;
  parentLabel: string;
  section: BankBudgetSection;
  summaryBucket: BankBudgetSummaryBucket;
  currentMonthAmount: number;
  currentMonthCount: number;
  rollingMonthlyAverage: number;
  annualTotal: number;
  annualCount: number;
  annualisedMonthlyEquivalent: number;
};

export type BankDisposableMetric = {
  monthlyActual: number;
  rollingMonthlyAverage: number;
  annualTotal: number;
  annualisedMonthlyEquivalent: number;
};

export type BankDisposableIncomeSummary = {
  income: BankDisposableMetric;
  essentialHouseholdCosts: BankDisposableMetric;
  committedDebtRepayments: BankDisposableMetric;
  savingsContributions: BankDisposableMetric;
  discretionarySpend: BankDisposableMetric;
  disposableIncome: BankDisposableMetric;
  leftoverAfterEssentialsOnly: BankDisposableMetric;
  leftoverAfterEssentialsAndSavings: BankDisposableMetric;
};

export type BankFeedRecurringCommitment = {
  merchant: string;
  categoryId: BankBudgetCategoryId;
  categoryLabel: string;
  count: number;
  cadence: BankRecurringCadence;
  confidence: MoneyConfidence;
  averageAmount: number;
  totalAmount: number;
  monthlyEquivalent: number;
  latestBookingDate: string;
  category: CanonicalBudgetCategory;
};

export type BankNormalisedTransaction = {
  transactionId: string | null;
  accountId: string | null;
  bookingDate: string;
  monthKey: string;
  amount: number;
  absoluteAmount: number;
  currency: string;
  rawLabel: string;
  merchantKey: string;
  merchantLabel: string;
  patternKey: string;
  normalisationKind: BankNormalisationKind;
  categoryId: BankBudgetCategoryId | null;
  categoryLabel: string | null;
  confidence: MoneyConfidence;
  needsReview: boolean;
  reviewReason: BankBudgetReviewReason | null;
  budgetCategory: CanonicalBudgetCategory | null;
  countsTowardsIncome: boolean;
  countsTowardsSpend: boolean;
  summaryBucket: BankBudgetSummaryBucket | null;
};

export type BankBudgetBreakdown = {
  currentMonthKey: string | null;
  rows: BankBudgetCategoryRow[];
  disposableIncome: BankDisposableIncomeSummary;
  recurringCommitments: BankFeedRecurringCommitment[];
  reviewQueue: BankBudgetReviewItem[];
};

export const BANK_BREAKDOWN_CATEGORY_OPTIONS = BANK_BREAKDOWN_CATEGORY_DEFINITIONS.map((entry) => ({
  value: entry.id,
  label: `${entry.parentLabel}: ${entry.label}`,
  parentLabel: entry.parentLabel,
  summaryBucket: entry.summaryBucket,
}));

export type BankFeedTransactionInput = {
  transactionId?: string | null;
  accountId?: string | null;
  bookingDate: string;
  amount: number;
  currency?: string | null;
  description?: string | null;
  merchantName?: string | null;
  categoryId?: BankBudgetCategoryId | null;
  categoryLabel?: string | null;
  confidence?: MoneyConfidence;
  needsReview?: boolean;
  reviewReason?: BankBudgetReviewReason | null;
  source?: 'bank' | 'amazon' | 'statement' | 'manual';
};

export type BankFeedCategorySummary = {
  category: CanonicalBudgetCategory;
  totalAmount: number;
  transactionCount: number;
};

export type BankFeedMonthlyCategoryAverage = {
  category: CanonicalBudgetCategory;
  averageAmount: number;
};

export type BankFeedCategoryOverride = {
  merchantKey?: string | null;
  merchantLabel?: string | null;
  category: CanonicalBudgetCategory | LegacyBudgetCategory;
};

export type BankFeedIncomeSource = {
  source: string;
  totalAmount: number;
  transactionCount: number;
};

export type BankFeedRecurringIncome = {
  source: string;
  categoryId: BankBudgetCategoryId;
  categoryLabel: string;
  count: number;
  cadence: BankRecurringCadence;
  confidence: MoneyConfidence;
  totalAmount: number;
  averageAmount: number;
  monthlyEquivalent: number;
  latestBookingDate: string;
  normalisationKind: BankNormalisationKind;
};

export type BankFeedRecurringMerchant = {
  merchant: string;
  category: CanonicalBudgetCategory;
  categoryId: BankBudgetCategoryId;
  categoryLabel: string;
  count: number;
  cadence: BankRecurringCadence;
  confidence: MoneyConfidence;
  totalAmount: number;
  averageAmount: number;
  monthlyEquivalent: number;
  latestBookingDate: string;
};

export type BankFeedAnnualRenewalCandidate = {
  merchant: string;
  category: CanonicalBudgetCategory;
  categoryId: BankBudgetCategoryId;
  categoryLabel: string;
  confidence: MoneyConfidence;
  annualAmount: number;
  latestBookingDate: string;
  reason: string;
};

export type BankFeedYearOnYearCategoryChange = {
  category: CanonicalBudgetCategory;
  currentAmount: number;
  previousAmount: number;
  differenceAmount: number;
};

export type BankFeedYearOnYearComparison = {
  currentMonthKey: string;
  previousYearMonthKey: string;
  currentOutgoingTotal: number;
  previousOutgoingTotal: number;
  differenceAmount: number;
  categoryChanges: BankFeedYearOnYearCategoryChange[];
};

export type BankFeedUnusualSpendFlag = {
  merchant: string;
  category: CanonicalBudgetCategory;
  amount: number;
  typicalAmount: number;
  bookingDate: string;
  message: string;
};

export type BankFeedOverspendSignal = {
  category: CanonicalBudgetCategory;
  currentAmount: number;
  averageAmount: number;
  differenceAmount: number;
  message: string;
};

export type MoneyHouseholdBenchmarkInput = {
  partnerName?: string | null;
  hasHouseholdShare?: boolean;
  childrenCount?: number;
};

export type MoneyHouseholdBenchmarkKey =
  | 'one_adult'
  | 'two_adults'
  | 'two_adults_one_child'
  | 'two_adults_two_children'
  | 'two_adults_three_children';

export type MoneyHouseholdBenchmark = {
  key: MoneyHouseholdBenchmarkKey;
  householdLabel: string;
  sourceLabel: string;
  exactMatch: boolean;
  note: string | null;
  monthlyByCategory: Partial<Record<CanonicalBudgetCategory, number>>;
};

export type MoneyBudgetBenchmarkComparisonRow = {
  category: CanonicalBudgetCategory;
  suggestedLimit: number;
  benchmarkMonthly: number;
  differenceAmount: number;
};

export type BankFeedBudgetSuggestionRow = {
  category: CanonicalBudgetCategory;
  currentLimit: number | null;
  currentMonthSpend: number;
  monthlyAverage: number;
  suggestedLimit: number;
  benchmarkMonthly: number | null;
  benchmarkDifference: number | null;
};

export type BankFeedBudgetSignals = {
  benchmark: MoneyHouseholdBenchmark | null;
  suggestedBudgetRows: BankFeedBudgetSuggestionRow[];
  overBudgetCategories: Array<{
    category: CanonicalBudgetCategory;
    spentAmount: number;
    budgetLimit: number;
    overBy: number;
  }>;
  unusualSpendFlags: string[];
};

export type BankFinanceAnalysisContext = {
  annualIncome?: number | null;
  partnerName?: string | null;
  hasHouseholdShare?: boolean;
  childrenCount?: number | null;
};

export type BankCreditorAnalysisView = {
  householdLabel: string;
  priorityCosts: BankDisposableMetric;
  protectedSavingsBuffer: BankDisposableMetric;
  currentDebtRepayments: BankDisposableMetric;
  priorityCostBalance: BankDisposableMetric;
  availableIncomeForCreditors: BankDisposableMetric;
  guidance: string;
};

export type BankMortgagePrepView = {
  householdLabel: string;
  annualIncomeOnProfile: number | null;
  declaredMonthlyIncomeOnProfile: number | null;
  netMonthlyIncomeFromBank: BankDisposableMetric;
  committedMonthlyOutgoings: BankDisposableMetric;
  otherCoreLivingCosts: BankDisposableMetric;
  headroomAfterCommitted: BankDisposableMetric;
  headroomAfterCoreLivingCosts: BankDisposableMetric;
  guidance: string;
};

export type BankFinanceAnalysisViews = {
  creditorView: BankCreditorAnalysisView;
  mortgagePrepView: BankMortgagePrepView;
};

export type BankFeedMerchantReviewReason = 'broad-merchant' | 'medium-confidence' | 'low-confidence';

export type FinanceReviewInboxSectionKey =
  | 'statement-import'
  | 'bank-review'
  | 'merchant-review'
  | 'amazon-review';

export type FinanceReviewInboxSection = {
  key: FinanceReviewInboxSectionKey;
  title: string;
  count: number;
  summary: string;
};

export type FinanceReviewInboxSummary = {
  totalCount: number;
  title: string;
  subtitle: string;
  sections: FinanceReviewInboxSection[];
};

export type BankFeedMerchantReviewCandidate = {
  merchantKey: string;
  merchantLabel: string;
  suggestedCategory: CanonicalBudgetCategory;
  confidence: MoneyConfidence;
  reason: BankFeedMerchantReviewReason;
  totalAmount: number;
  averageAmount: number;
  transactionCount: number;
  latestBookingDate: string;
};

export type ConnectedBankBudgetAutoRefreshInput = {
  enabled: boolean;
  latestBankSyncAt: string | null;
  lastAppliedSyncAt: string | null;
  suggestedBudgetRowCount: number;
};

export type BankFeedAnalysis = {
  transactionCount: number;
  coverageStart: string | null;
  coverageEnd: string | null;
  coverageDays: number;
  monthsCovered: number;
  likelyPartialYear: boolean;
  latestMonthKey: string | null;
  primaryCurrency: string;
  incomingTotal: number;
  outgoingTotal: number;
  netTotal: number;
  spendingByCategory: BankFeedCategorySummary[];
  currentMonthSpendingByCategory: BankFeedCategorySummary[];
  monthlyAverageByCategory: BankFeedMonthlyCategoryAverage[];
  suggestedBudget: Record<CanonicalBudgetCategory, number>;
  incomeBySource: BankFeedIncomeSource[];
  recurringIncomes: BankFeedRecurringIncome[];
  recurringOutgoings: BankFeedRecurringMerchant[];
  annualRenewalCandidates: BankFeedAnnualRenewalCandidate[];
  budgetBreakdown: BankBudgetBreakdown;
  normalisedTransactions: BankNormalisedTransaction[];
  yearOnYear: BankFeedYearOnYearComparison | null;
  unusualSpendFlags: BankFeedUnusualSpendFlag[];
  overspendSignals: BankFeedOverspendSignal[];
  broadMerchantNames: string[];
};

export type BankFeedAnalysisOptions = {
  categoryRules?: readonly BankCategoryRule[] | null;
  categoryOverrides?: readonly BankFeedCategoryOverride[] | null;
  amazonCategoryRules?: readonly AmazonCategoryRule[] | null;
};

function zeroDisposableMetric(): BankDisposableMetric {
  return {
    monthlyActual: 0,
    rollingMonthlyAverage: 0,
    annualTotal: 0,
    annualisedMonthlyEquivalent: 0,
  };
}

function addDisposableMetrics(left: BankDisposableMetric, right: BankDisposableMetric): BankDisposableMetric {
  return {
    monthlyActual: roundCurrency(left.monthlyActual + right.monthlyActual),
    rollingMonthlyAverage: roundCurrency(left.rollingMonthlyAverage + right.rollingMonthlyAverage),
    annualTotal: roundCurrency(left.annualTotal + right.annualTotal),
    annualisedMonthlyEquivalent: roundCurrency(left.annualisedMonthlyEquivalent + right.annualisedMonthlyEquivalent),
  };
}

function subtractDisposableMetrics(left: BankDisposableMetric, right: BankDisposableMetric): BankDisposableMetric {
  return {
    monthlyActual: roundCurrency(left.monthlyActual - right.monthlyActual),
    rollingMonthlyAverage: roundCurrency(left.rollingMonthlyAverage - right.rollingMonthlyAverage),
    annualTotal: roundCurrency(left.annualTotal - right.annualTotal),
    annualisedMonthlyEquivalent: roundCurrency(left.annualisedMonthlyEquivalent - right.annualisedMonthlyEquivalent),
  };
}

function sumBudgetBreakdownRows(
  rows: readonly BankBudgetCategoryRow[],
  predicate: (row: BankBudgetCategoryRow) => boolean,
): BankDisposableMetric {
  return rows.reduce<BankDisposableMetric>((sum, row) => {
    if (!predicate(row)) return sum;
    return addDisposableMetrics(sum, {
      monthlyActual: row.currentMonthAmount,
      rollingMonthlyAverage: row.rollingMonthlyAverage,
      annualTotal: row.annualTotal,
      annualisedMonthlyEquivalent: row.annualisedMonthlyEquivalent,
    });
  }, zeroDisposableMetric());
}

function buildFinanceHouseholdLabel({
  partnerName,
  hasHouseholdShare,
  childrenCount,
}: Pick<BankFinanceAnalysisContext, 'partnerName' | 'hasHouseholdShare' | 'childrenCount'>) {
  const adults = partnerName?.trim() || hasHouseholdShare ? 2 : 1;
  const children = Math.max(0, Math.round(childrenCount || 0));
  return `${adults} adult${adults === 1 ? '' : 's'}${children ? `, ${children} child${children === 1 ? '' : 'ren'}` : ''}`;
}

function formatFinanceCurrencyText(value: number | null | undefined, currency = 'GBP') {
  if (value == null || !Number.isFinite(value)) return 'Not set';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

const MONEY_CATEGORY_KEYWORDS: Array<{ category: CanonicalBudgetCategory; words: string[] }> = [
  { category: 'Rent / mortgage', words: ['rent', 'mortgage', 'landlord', 'housing association'] },
  { category: 'Council tax', words: ['council tax'] },
  { category: 'Service charge / ground rent', words: ['service charge', 'ground rent'] },
  { category: 'Gas / electric', words: ['energy', 'electric', 'gas', 'octopus', 'edf', 'eon', 'british gas', 'ovo', 'scottish power'] },
  { category: 'Water', words: ['water', 'thames water', 'severn trent', 'united utilities'] },
  { category: 'Broadband', words: ['broadband', 'internet', 'virgin media', 'bt', 'talktalk', 'sky broadband'] },
  { category: 'Mobile', words: ['mobile', 'vodafone', 'o2', 'three', 'ee', 'giffgaff', 'xavier mobile'] },
  { category: 'Food & housekeeping', words: ['tesco', 'sainsbury', 'asda', 'aldi', 'lidl', 'waitrose', 'morrisons', 'coop', 'co-op', 'grocery', 'grocer', 'ocado', 'marks spencer', 'm&s', 'iceland'] },
  { category: 'Fuel', words: ['fuel', 'petrol', 'diesel', 'shell', 'esso', 'bp', 'texaco'] },
  { category: 'Public transport', words: ['uber', 'train', 'rail', 'tfl', 'bus', 'transport', 'bolt'] },
  { category: 'Parking / tolls', words: ['parking', 'toll', 'ringgo', 'ncp'] },
  { category: 'Vehicle tax / MOT / servicing', words: ['dvla', 'mot', 'garage', 'service', 'tyre', 'kwik fit', 'halfords'] },
  { category: 'Childcare & school', words: ['nursery', 'childcare', 'child minder', 'childminder', 'babysit', 'breakfast club', 'after school', 'holiday club', 'school', 'uniform', 'pta', 'trip', 'school dinner', 'tutor', 'tuition'] },
  { category: 'Insurance', words: ['insurance', 'aviva', 'admiral', 'direct line', 'axa'] },
  { category: 'Health & medical', words: ['pharmacy', 'chemist', 'dentist', 'clinic', 'hospital', 'optician', 'specsavers'] },
  { category: 'Debt repayments', words: ['klarna', 'loan repayment', 'premfina', 'bumper.co', 'your plan card', 'creation.co', 'debt', 'zopa'] },
  { category: 'Subscriptions', words: ['spotify', 'netflix', 'disney', 'amazon prime', 'prime video', 'apple.com/bill', 'youtube premium', 'audible', 'national trust', 'passport club'] },
  { category: 'Clothing & personal care', words: ['primark', 'next', 'zara', 'h&m', 'barber', 'salon', 'cosmetic', 'superdrug'] },
  { category: 'Pets', words: ['pets at home', 'pet', 'vet', 'veterinary', 'vets4pets'] },
  { category: 'Work / self-employed', words: ['hmrc', 'companies house', 'companieshouse', 'adobe', 'zoom', 'slack', 'domain', 'hosting', 'cloudflare', 'github', 'figma'] },
  { category: 'Savings', words: ['savings', 'isa', 'moneybox', 'chip', 'plum', 'ns&i'] },
  { category: 'Leisure / entertainment', words: ['cinema', 'odeon', 'theatre', 'restaurant', 'takeaway', 'pub', 'gym', 'coffee', 'hotel', 'airbnb', 'mcdonalds'] },
];

const BROAD_MERCHANT_HINTS = ['amazon', 'amzn', 'paypal', 'apple.com/bill', 'ebay', 'etsy', 'sumup', 'squareup', 'sq'];

const ANNUAL_RENEWAL_HINTS = [
  'insurance',
  'premfina',
  'passport club',
  'national trust',
  'companies house',
  'companieshouse',
  'cloudflare',
  'domain',
  'hosting',
  'membership',
  'mot',
  'dvla',
  'vehicle tax',
];

const MERCHANT_DISPLAY_RULES: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /\btesco\b/, label: 'Tesco' },
  { pattern: /\bsainsburys?\b/, label: "Sainsbury's" },
  { pattern: /\basda\b/, label: 'Asda' },
  { pattern: /\baldi\b/, label: 'Aldi' },
  { pattern: /\blidl\b/, label: 'Lidl' },
  { pattern: /\bwaitrose\b/, label: 'Waitrose' },
  { pattern: /\bmorrisons?\b/, label: 'Morrisons' },
  { pattern: /\bco ?op\b/, label: 'Co-op' },
  { pattern: /\bocado\b/, label: 'Ocado' },
  { pattern: /\bamazon\b/, label: 'Amazon' },
  { pattern: /\bpaypal\b/, label: 'PayPal' },
  { pattern: /\bapple\b/, label: 'Apple' },
  { pattern: /\bspotify\b/, label: 'Spotify' },
  { pattern: /\bnetflix\b/, label: 'Netflix' },
  { pattern: /\buber\b/, label: 'Uber' },
  { pattern: /\btfl\b/, label: 'TfL' },
  { pattern: /\bbritish gas\b/, label: 'British Gas' },
  { pattern: /\boctopus\b/, label: 'Octopus Energy' },
  { pattern: /\bvirgin media\b/, label: 'Virgin Media' },
  { pattern: /\bvodafone\b/, label: 'Vodafone' },
];

// Source: ONS reference 2302, "Detailed household expenditure by gross income group
// for different household compositions", released September 2, 2024. We use the
// "All households" weekly spend column from the FYE 2023 household-composition cuts.
const MONEY_HOUSEHOLD_BENCHMARK_SOURCE = 'ONS household spending, FYE 2023';
const MONEY_HOUSEHOLD_WEEKLY_BENCHMARKS: Record<
  MoneyHouseholdBenchmarkKey,
  {
    householdLabel: string;
    weeklyByCategory: Partial<Record<CanonicalBudgetCategory, number>>;
  }
> = {
  one_adult: {
    householdLabel: '1 adult',
    weeklyByCategory: {
      'Food & housekeeping': 44.8,
      'Gas / electric': 62.3,
      Water: 11.3,
      Broadband: 22.6,
      Mobile: 17,
      Fuel: 25.8,
      'Public transport': 21.5,
      'Parking / tolls': 3,
      'Vehicle tax / MOT / servicing': 11.1,
    },
  },
  two_adults: {
    householdLabel: '2 adults',
    weeklyByCategory: {
      'Food & housekeeping': 66.9,
      'Gas / electric': 74.3,
      Water: 13.5,
      Broadband: 27,
      Mobile: 20.2,
      Fuel: 48.2,
      'Public transport': 40.2,
      'Parking / tolls': 5.7,
      'Vehicle tax / MOT / servicing': 20.7,
    },
  },
  two_adults_one_child: {
    householdLabel: '2 adults, 1 child',
    weeklyByCategory: {
      'Food & housekeeping': 76.5,
      'Gas / electric': 57.6,
      Water: 10.5,
      Broadband: 20.9,
      Mobile: 15.7,
      Fuel: 48.8,
      'Public transport': 40.6,
      'Parking / tolls': 5.8,
      'Vehicle tax / MOT / servicing': 20.9,
      'Childcare & school': 11.2,
    },
  },
  two_adults_two_children: {
    householdLabel: '2 adults, 2 children',
    weeklyByCategory: {
      'Food & housekeeping': 93.8,
      'Gas / electric': 65.4,
      Water: 11.9,
      Broadband: 23.8,
      Mobile: 17.8,
      Fuel: 58.7,
      'Public transport': 48.9,
      'Parking / tolls': 7,
      'Vehicle tax / MOT / servicing': 25.2,
      'Childcare & school': 16.9,
    },
  },
  two_adults_three_children: {
    householdLabel: '2 adults, 3 children',
    weeklyByCategory: {
      'Food & housekeeping': 107.5,
      'Gas / electric': 58.4,
      Water: 10.6,
      Broadband: 21.3,
      Mobile: 16,
      Fuel: 62.3,
      'Public transport': 51.9,
      'Parking / tolls': 7.4,
      'Vehicle tax / MOT / servicing': 26.7,
    },
  },
};

export const DEFAULT_BUDGET_LIMITS = [
  { category: 'Rent / mortgage', limit: 900 },
  { category: 'Council tax', limit: 160 },
  { category: 'Service charge / ground rent', limit: 40 },
  { category: 'Gas / electric', limit: 120 },
  { category: 'Water', limit: 40 },
  { category: 'Broadband', limit: 35 },
  { category: 'Mobile', limit: 35 },
  { category: 'Food & housekeeping', limit: 400 },
  { category: 'Fuel', limit: 150 },
  { category: 'Public transport', limit: 120 },
  { category: 'Parking / tolls', limit: 30 },
  { category: 'Vehicle tax / MOT / servicing', limit: 60 },
  { category: 'Childcare & school', limit: 250 },
  { category: 'Insurance', limit: 80 },
  { category: 'Health & medical', limit: 60 },
  { category: 'Debt repayments', limit: 150 },
  { category: 'Subscriptions', limit: 40 },
  { category: 'Clothing & personal care', limit: 60 },
  { category: 'Pets', limit: 50 },
  { category: 'Work / self-employed', limit: 80 },
  { category: 'Savings', limit: 100 },
  { category: 'Leisure / entertainment', limit: 120 },
  { category: 'Other essential', limit: 100 },
  { category: 'Other discretionary', limit: 100 },
] as const;

function roundCurrency(value: number) {
  return Math.round(value * 100) / 100;
}

function roundBudgetLimit(value: number) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value / 10) * 10;
}

function monthlyBudgetFromWeekly(value: number | null | undefined) {
  if (!Number.isFinite(value || 0)) return 0;
  return roundBudgetLimit(((value || 0) * 52) / 12);
}

function titleCase(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const BANK_BREAKDOWN_CATEGORY_META = new Map(
  BANK_BREAKDOWN_CATEGORY_DEFINITIONS.map((entry) => [entry.id, entry]),
);

const BANK_NORMALISATION_KEYWORDS = {
  salary: ['salary', 'payroll', 'wages', 'wage', 'paye'],
  benefits: [
    'universal credit',
    'child benefit',
    'working tax credit',
    'child tax credit',
    'pip',
    'dwp',
    'esa',
    'attendance allowance',
    'carer',
    'housing benefit',
    'jobseekers allowance',
    'jsa',
  ],
  maintenance: ['child maintenance', 'cms', 'csa maintenance', 'maintenance'],
  refund: ['refund', 'reversal', 'chargeback', 'returned', 'return'],
  loanDrawdown: ['loan drawdown', 'loan advance', 'loan payout', 'loan disbursal'],
  bankFee: ['account fee', 'bank fee', 'service charge', 'paid referral fee', 'overdraft fee', 'unpaid item fee'],
  bankInterest: ['interest paid', 'credit interest', 'gross interest'],
  creditCardRepayment: ['credit card payment', 'card payment', 'barclaycard', 'mbna', 'amex', 'american express', 'capital one', 'aqua', 'lloyds card', 'halifax card', 'newday', 'vanquis', 'klarna', 'premfina', 'bumper.co', 'creation.co', 'your plan card'],
  savingsTransfer: ['savings', 'cash isa', 'isa', 'moneybox', 'chip', 'plum', 'monzo savings', 'pot transfer'],
  transfer: ['faster payment', 'bank transfer', 'transfer', 'standing order', 'sent from', 'received from'],
  cashWithdrawal: ['cash withdrawal', 'cash wd', 'atm', 'cash machine'],
} as const;

const BANK_CATEGORY_KEYWORD_RULES: Array<{
  categoryId: BankBudgetCategoryId;
  words: string[];
  confidence: MoneyConfidence;
}> = [
  {
    categoryId: 'housing_rent_mortgage',
    words: ['rent', 'mortgage', 'landlord', 'housing assoc', 'housing association'],
    confidence: 'high',
  },
  {
    categoryId: 'housing_council_tax',
    words: ['council tax'],
    confidence: 'high',
  },
  {
    categoryId: 'housing_service_charge_ground_rent',
    words: ['service charge', 'ground rent'],
    confidence: 'high',
  },
  {
    categoryId: 'utilities_gas_electric',
    words: ['octopus', 'british gas', 'edf', 'eon', 'scottish power', 'ovo', 'utilita', 'utility warehouse', 'gas', 'electric'],
    confidence: 'high',
  },
  {
    categoryId: 'utilities_water',
    words: ['water', 'thames water', 'severn trent', 'united utilities', 'anglian water', 'welsh water', 'yorkshire water', 'southern water', 'south west water', 'northumbrian water'],
    confidence: 'high',
  },
  {
    categoryId: 'utilities_broadband',
    words: ['broadband', 'internet', 'virgin media', 'bt', 'talktalk', 'plusnet', 'sky broadband'],
    confidence: 'high',
  },
  {
    categoryId: 'utilities_mobile',
    words: ['vodafone', 'o2', 'three', 'giffgaff', 'ee', 'lebara', 'mobile', 'xavier mobile'],
    confidence: 'high',
  },
  {
    categoryId: 'food_housekeeping',
    words: ['tesco', 'sainsbury', 'asda', 'aldi', 'lidl', 'waitrose', 'morrisons', 'co-op', 'coop', 'ocado', 'iceland', 'marks spencer', 'm&s', 'grocery', 'grocer', 'farmfoods'],
    confidence: 'high',
  },
  {
    categoryId: 'travel_fuel',
    words: ['shell', 'esso', 'bp', 'texaco', 'petrol', 'diesel', 'fuel'],
    confidence: 'high',
  },
  {
    categoryId: 'travel_public_transport',
    words: ['tfl', 'trainline', 'national rail', 'bus', 'rail', 'stagecoach', 'arriva', 'tram'],
    confidence: 'high',
  },
  {
    categoryId: 'travel_parking_tolls',
    words: ['parking', 'ringgo', 'ncp', 'paybyphone', 'toll', 'dart charge', 'merseyflow'],
    confidence: 'high',
  },
  {
    categoryId: 'travel_vehicle_costs',
    words: ['dvla', 'mot', 'kwik fit', 'halfords', 'tyre', 'service', 'garage'],
    confidence: 'medium',
  },
  {
    categoryId: 'childcare',
    words: ['nursery', 'childcare', 'childminder', 'child minder', 'holiday club', 'after school club', 'breakfast club', 'babysit', 'nanny'],
    confidence: 'high',
  },
  {
    categoryId: 'school_costs',
    words: ['school', 'uniform', 'pta', 'school dinner', 'trip', 'tuition', 'tutor'],
    confidence: 'high',
  },
  {
    categoryId: 'insurance',
    words: ['insurance', 'aviva', 'admiral', 'direct line', 'lv=', 'axa'],
    confidence: 'high',
  },
  {
    categoryId: 'debt_repayments',
    words: ['klarna', 'premfina', 'bumper.co', 'creation.co', 'your plan card', 'loan repayment', 'zopa', 'credit card payment', 'barclaycard', 'mbna', 'amex', 'american express', 'capital one', 'aqua', 'lloyds card', 'halifax card', 'newday', 'vanquis'],
    confidence: 'medium',
  },
  {
    categoryId: 'health_medical',
    words: ['pharmacy', 'chemist', 'boots opticians', 'specsavers', 'dentist', 'optician', 'doctor', 'clinic', 'hospital'],
    confidence: 'high',
  },
  {
    categoryId: 'subscriptions',
    words: ['spotify', 'netflix', 'disney', 'amazon prime', 'prime video', 'apple.com/bill', 'google one', 'audible', 'youtube premium', 'xbox', 'playstation', 'national trust', 'passport club'],
    confidence: 'high',
  },
  {
    categoryId: 'clothing_personal_care',
    words: ['primark', 'next', 'zara', 'h&m', 'barber', 'hair', 'salon', 'superdrug'],
    confidence: 'medium',
  },
  {
    categoryId: 'pets',
    words: ['pets at home', 'vet', 'vets4pets', 'veterinary', 'pet'],
    confidence: 'high',
  },
  {
    categoryId: 'work_self_employed',
    words: ['hmrc', 'companies house', 'companieshouse', 'adobe', 'zoom', 'slack', 'domain', 'hosting', 'cloudflare', 'github', 'figma'],
    confidence: 'medium',
  },
  {
    categoryId: 'leisure_entertainment',
    words: ['cinema', 'odeon', 'theatre', 'restaurant', 'takeaway', 'pub', 'gym', 'coffee', 'airbnb', 'booking.com', 'hotel', 'mcdonalds'],
    confidence: 'medium',
  },
];

function getBankBudgetCategoryMeta(categoryId: BankBudgetCategoryId | null | undefined) {
  if (!categoryId) return null;
  return BANK_BREAKDOWN_CATEGORY_META.get(categoryId) || null;
}

function getBudgetCategoryFromBreakdownCategoryId(categoryId: BankBudgetCategoryId | null | undefined) {
  const meta = getBankBudgetCategoryMeta(categoryId);
  return meta?.budgetCategory || null;
}

export function formatBankBudgetCategoryLabel(categoryId: BankBudgetCategoryId | null | undefined) {
  const meta = getBankBudgetCategoryMeta(categoryId);
  return meta ? `${meta.parentLabel}: ${meta.label}` : 'Needs review';
}

export function describeBankFeedMerchantReviewReason(reason: BankFeedMerchantReviewReason) {
  if (reason === 'broad-merchant') {
    return 'This merchant stays broad in bank data, so a confirmation keeps the budget cleaner.';
  }

  if (reason === 'medium-confidence') {
    return 'Adulting has a best guess here, but it is worth confirming once.';
  }

  return 'The bank reference is too vague for Adulting to trust without your confirmation.';
}

export function describeBankBudgetReviewReason(reason: BankBudgetReviewReason) {
  if (reason === 'broad-merchant') {
    return 'The bank reference is still broad here, so it needs a human check once.';
  }

  if (reason === 'cash-withdrawal') {
    return 'Cash withdrawals can be hard to place automatically, so Adulting keeps them ready for review.';
  }

  if (reason === 'unclear-transfer') {
    return 'This looks transfer-like, but Adulting could not safely tell whether it was your own money moving around.';
  }

  if (reason === 'unclear-income') {
    return 'This looks like money in, but Adulting cannot safely tell yet whether it is regular income, support, or a one-off payment.';
  }

  return 'Adulting is not confident enough to place this without your say-so.';
}

export function mapBudgetCategoryToBankBreakdownCategoryId(
  category: CanonicalBudgetCategory | LegacyBudgetCategory,
): BankBudgetCategoryId {
  return mapLegacyCategoryToBreakdownCategoryId(category);
}

export function buildFinanceReviewInboxSummary({
  bankReviewCount,
  merchantReviewCount,
  amazonReviewCount,
  statementImportNeedsReview = false,
  statementImportFallbackRecommended = false,
}: {
  bankReviewCount: number;
  merchantReviewCount: number;
  amazonReviewCount: number;
  statementImportNeedsReview?: boolean;
  statementImportFallbackRecommended?: boolean;
}): FinanceReviewInboxSummary | null {
  const sections: FinanceReviewInboxSection[] = [];

  if (statementImportNeedsReview) {
    sections.push({
      key: 'statement-import',
      title: statementImportFallbackRecommended ? 'Safer statement path' : 'Statement import to check',
      count: 1,
      summary: statementImportFallbackRecommended
        ? 'The latest statement preview should stay in review or move to CSV / bank feed before saving.'
        : 'The latest statement preview still needs a human check before saving.',
    });
  }

  if (bankReviewCount > 0) {
    sections.push({
      key: 'bank-review',
      title: 'Uncertain bank items',
      count: bankReviewCount,
      summary: 'Bank rows Adulting could not safely place yet.',
    });
  }

  if (merchantReviewCount > 0) {
    sections.push({
      key: 'merchant-review',
      title: 'Merchants to confirm',
      count: merchantReviewCount,
      summary: 'Broad or vague merchants that need one saved category.',
    });
  }

  if (amazonReviewCount > 0) {
    sections.push({
      key: 'amazon-review',
      title: 'Amazon items to place',
      count: amazonReviewCount,
      summary: 'Order items that still need a human category check.',
    });
  }

  if (!sections.length) {
    return null;
  }

  const totalCount = sections.reduce((sum, section) => sum + section.count, 0);

  return {
    totalCount,
    title: 'Finance review inbox',
    subtitle:
      sections.length === 1
        ? 'Keep this final finance check in one place so the rest of Money stays calm.'
        : 'Keep the uncertain finance items in one place so the rest of Money stays calm.',
    sections,
  };
}

function mapLegacyCategoryToBreakdownCategoryId(category: CanonicalBudgetCategory | LegacyBudgetCategory): BankBudgetCategoryId {
  switch (category) {
    case 'Childcare':
      return 'childcare';
    case 'Food & groceries':
      return 'food_housekeeping';
    case 'School costs':
      return 'school_costs';
    case 'Utilities':
      return 'utilities_other';
    case 'Transport':
      return 'travel_other';
    case 'Other':
    default:
      return 'other_discretionary';
  }
}

export function mapBreakdownCategoryIdToLegacyCategory(categoryId: BankBudgetCategoryId): LegacyBudgetCategory {
  const meta = getBankBudgetCategoryMeta(categoryId);
  if (!meta) return 'Other';

  if (meta.section === 'childcare-school') {
    return categoryId === 'school_costs' ? 'School costs' : 'Childcare';
  }

  if (meta.section === 'food-housekeeping') {
    return 'Food & groceries';
  }

  if (meta.section === 'utilities' || meta.section === 'housing') {
    return 'Utilities';
  }

  if (meta.section === 'travel') {
    return 'Transport';
  }

  return 'Other';
}

function normaliseMoneyPattern(description: string) {
  return description
    .toLowerCase()
    .replace(/[0-9*#/_-]+/g, ' ')
    .replace(/\b(?:ref|reference|pending|online|mobile|mas|tfr|bp|cr|dr)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function textMatchesKeyword(text: string, word: string) {
  const pattern = new RegExp(`(?:^|[^a-z0-9])${escapeRegex(word).replace(/\s+/g, '\\s+')}(?=$|[^a-z0-9])`, 'i');
  return pattern.test(text);
}

function hasKeyword(text: string, words: readonly string[]) {
  return words.some((word) => textMatchesKeyword(text, word));
}

function safeDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysBetween(start: string | null, end: string | null) {
  if (!start || !end) return 0;
  const startDate = safeDate(start);
  const endDate = safeDate(end);
  if (!startDate || !endDate) return 0;
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
}

function getEntryLabel(input: Pick<BankFeedTransactionInput, 'merchantName' | 'description'>) {
  return [input.merchantName, input.description].filter(Boolean).join(' ').trim();
}

function buildBankCategoryRuleMaps(options: BankFeedAnalysisOptions = {}) {
  const transactionRules = new Map<string, BankBudgetCategoryId>();
  const merchantRules = new Map<string, BankBudgetCategoryId>();
  const patternRules = new Map<string, BankBudgetCategoryId>();

  for (const rule of options.categoryRules || []) {
    if (rule.scope === 'transaction' && rule.transactionId) {
      transactionRules.set(rule.transactionId, rule.categoryId);
      continue;
    }

    if (rule.scope === 'pattern' && rule.patternKey) {
      patternRules.set(normaliseMoneyPattern(rule.patternKey), rule.categoryId);
      continue;
    }

    if (rule.scope === 'merchant' && (rule.merchantKey || rule.merchantLabel)) {
      const merchantKey = normaliseMoneyMerchant(rule.merchantKey || rule.merchantLabel || '');
      if (merchantKey) {
        merchantRules.set(merchantKey, rule.categoryId);
      }
    }
  }

  for (const entry of options.categoryOverrides || []) {
    const merchantKey = normaliseMoneyMerchant(entry.merchantKey || entry.merchantLabel || '');
    if (!merchantKey || merchantRules.has(merchantKey)) continue;
    merchantRules.set(merchantKey, mapLegacyCategoryToBreakdownCategoryId(entry.category));
  }

  return { transactionRules, merchantRules, patternRules };
}

export function normaliseMoneyMerchant(description: string) {
  return description
    .toLowerCase()
    .replace(/\b(card|payment|debit|credit|purchase|pos|contactless|visa|mastercard|ref|reference|faster|standing|order|direct|dd|to|from|transfer|txn|transaction|pending|mobile|online|banking|bacs|fpo|bank|ltd|limited|plc|mas|tfr|bp|cr|dr)\b/g, ' ')
    .replace(/\b(amznmktplace|amz|paypal|sumup|squareup|sq|tst)\b/g, ' ')
    .replace(/[0-9*#/_-]+/g, ' ')
    .replace(/\b(?:co|uk|gb|gbr|london)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getMoneyMerchantDisplayName(description: string) {
  const normalised = normaliseMoneyMerchant(description);
  if (!normalised) return 'Bank transaction';

  const directMatch = MERCHANT_DISPLAY_RULES.find((candidate) => candidate.pattern.test(normalised));
  if (directMatch) {
    return directMatch.label;
  }

  const shortened = normalised
    .split(' ')
    .filter(Boolean)
    .slice(0, 3)
    .join(' ');

  return titleCase(shortened || normalised);
}

function normaliseIncomeSourceKey(description: string) {
  const tokens = normaliseMoneyPattern(description)
    .split(' ')
    .filter(Boolean);

  while (tokens.length > 1 && /^[a-z]{1,3}$/.test(tokens[0]) && !['dwp', 'hmrc', 'uc', 'ca', 'pip', 'jsa'].includes(tokens[0])) {
    tokens.shift();
  }

  return tokens.join(' ');
}

function getMoneyIncomeSourceDisplayName(description: string) {
  const normalised = normaliseIncomeSourceKey(description);
  if (!normalised) return getMoneyMerchantDisplayName(description);
  return titleCase(normalised.split(' ').slice(0, 4).join(' '));
}

function inferBreakdownCategoryFromKeywords(normalisedLabel: string) {
  for (const candidate of BANK_CATEGORY_KEYWORD_RULES) {
    if (candidate.words.some((word) => textMatchesKeyword(normalisedLabel, word))) {
      return {
        categoryId: candidate.categoryId,
        confidence: candidate.confidence,
      };
    }
  }

  if (BROAD_MERCHANT_HINTS.some((hint) => normalisedLabel.includes(hint))) {
    return { categoryId: 'other_discretionary' as BankBudgetCategoryId, confidence: 'medium' as MoneyConfidence };
  }

  return { categoryId: 'other_discretionary' as BankBudgetCategoryId, confidence: 'low' as MoneyConfidence };
}

function inferMoneyCategoryFromBreakdown(description: string): { category: CanonicalBudgetCategory; confidence: MoneyConfidence } {
  const normalised = description.toLowerCase();
  for (const candidate of MONEY_CATEGORY_KEYWORDS) {
    if (candidate.words.some((word) => textMatchesKeyword(normalised, word))) {
      return { category: candidate.category, confidence: 'high' };
    }
  }

  const inferred = inferBreakdownCategoryFromKeywords(normaliseMoneyPattern(description));
  return {
    category: getBudgetCategoryFromBreakdownCategoryId(inferred.categoryId) || DEFAULT_EXPENSE_CATEGORY,
    confidence: inferred.confidence,
  };
}

export function inferMoneyCategory(description: string): { category: CanonicalBudgetCategory; confidence: MoneyConfidence } {
  return inferMoneyCategoryFromBreakdown(description);
}

function buildAmountMatchIndex(transactions: readonly BankFeedTransactionInput[]) {
  const index = new Map<string, BankFeedTransactionInput[]>();

  for (const transaction of transactions) {
    if (!Number.isFinite(transaction.amount) || transaction.amount === 0) continue;
    const key = Math.abs(transaction.amount).toFixed(2);
    const current = index.get(key) || [];
    current.push(transaction);
    index.set(key, current);
  }

  return index;
}

function hasOppositeAmountMatch(
  transaction: BankFeedTransactionInput,
  amountIndex: ReadonlyMap<string, readonly BankFeedTransactionInput[]>,
) {
  const peers = amountIndex.get(Math.abs(transaction.amount).toFixed(2)) || [];
  return peers.some((peer) => {
    if (peer === transaction) return false;
    if (peer.amount === 0 || Math.sign(peer.amount) === Math.sign(transaction.amount)) return false;
    if (transaction.accountId && peer.accountId && transaction.accountId === peer.accountId) return false;
    return Math.abs(daysBetween(transaction.bookingDate, peer.bookingDate)) <= 3;
  });
}

function inferNormalisationKind(
  transaction: BankFeedTransactionInput,
  rawLabel: string,
  amountIndex: ReadonlyMap<string, readonly BankFeedTransactionInput[]>,
): BankNormalisationKind {
  const normalisedLabel = rawLabel.toLowerCase();
  const hasTransferMatch = hasOppositeAmountMatch(transaction, amountIndex);
  const incoming = transaction.amount > 0;

  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.salary)) return 'salary';
  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.benefits)) return 'benefits';
  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.maintenance)) return 'maintenance';
  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.refund)) return 'refund';
  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.loanDrawdown)) return 'loan-drawdown';
  if (incoming && hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.bankInterest)) return 'bank-interest';

  if (hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.cashWithdrawal)) return 'cash-withdrawal';
  if (hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.bankFee)) return 'bank-fee';
  if (hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.creditCardRepayment)) return 'credit-card-repayment';
  if (hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.savingsTransfer)) return 'savings-transfer';
  if (hasTransferMatch) return 'internal-transfer';

  if (hasKeyword(normalisedLabel, BANK_NORMALISATION_KEYWORDS.transfer)) {
    return 'incoming-transfer';
  }

  if (incoming) return 'income';
  return 'spend';
}

function resolveCategoryIdFromNormalisation(kind: BankNormalisationKind): BankBudgetCategoryId | null {
  switch (kind) {
    case 'salary':
      return 'income_salary';
    case 'benefits':
      return 'income_benefits';
    case 'maintenance':
      return 'income_maintenance';
    case 'income':
    case 'bank-interest':
      return 'income_other';
    case 'bank-fee':
      return 'other_essential';
    case 'credit-card-repayment':
      return 'debt_repayments';
    case 'savings-transfer':
      return 'savings';
    default:
      return null;
  }
}

function resolveReviewReason(
  confidence: MoneyConfidence,
  merchantKey: string,
  patternKey: string,
  normalisationKind: BankNormalisationKind,
): BankBudgetReviewReason | null {
  if (normalisationKind === 'cash-withdrawal') return 'cash-withdrawal';
  if (normalisationKind === 'incoming-transfer') return 'unclear-transfer';
  if (normalisationKind === 'income' && confidence === 'low') return 'unclear-income';
  if (BROAD_MERCHANT_HINTS.some((hint) => merchantKey.includes(hint) || patternKey.includes(hint))) {
    return 'broad-merchant';
  }
  if (confidence === 'low') return 'low-confidence';
  return null;
}

function buildNormalisedTransaction(
  transaction: BankFeedTransactionInput,
  amountIndex: ReadonlyMap<string, readonly BankFeedTransactionInput[]>,
  ruleMaps: ReturnType<typeof buildBankCategoryRuleMaps>,
): BankNormalisedTransaction {
  const rawLabel = getEntryLabel(transaction) || 'Bank transaction';
  const merchantKey = normaliseMoneyMerchant(rawLabel);
  const merchantLabel = getMoneyMerchantDisplayName(rawLabel);
  const patternKey = normaliseMoneyPattern(rawLabel);
  const normalisationKind = inferNormalisationKind(transaction, rawLabel, amountIndex);
  const explicitCategoryId = transaction.categoryId || null;
  const ruleCategoryId =
    (transaction.transactionId ? ruleMaps.transactionRules.get(transaction.transactionId) : null)
    || (patternKey ? ruleMaps.patternRules.get(patternKey) : null)
    || (merchantKey ? ruleMaps.merchantRules.get(merchantKey) : null)
    || null;

  let categoryId = explicitCategoryId || ruleCategoryId || resolveCategoryIdFromNormalisation(normalisationKind);
  let confidence: MoneyConfidence = explicitCategoryId ? (transaction.confidence || 'high') : ruleCategoryId ? 'high' : 'medium';

  if (!explicitCategoryId && !ruleCategoryId && normalisationKind === 'income') {
    confidence = 'low';
  }

  if (!categoryId && normalisationKind === 'spend') {
    const inferred = inferBreakdownCategoryFromKeywords(patternKey || rawLabel.toLowerCase());
    categoryId = inferred.categoryId;
    confidence = inferred.confidence;
  } else if (!categoryId) {
    confidence = 'low';
  }

  const categoryMeta = getBankBudgetCategoryMeta(categoryId);
  const reviewReason: BankBudgetReviewReason | null =
    transaction.reviewReason || resolveReviewReason(confidence, merchantKey, patternKey, normalisationKind);
  const countsTowardsIncome = Boolean(
    categoryMeta?.summaryBucket === 'income'
    && normalisationKind !== 'refund'
    && normalisationKind !== 'loan-drawdown'
    && normalisationKind !== 'incoming-transfer'
    && normalisationKind !== 'internal-transfer',
  );
  const countsTowardsSpend = Boolean(
    transaction.amount < 0
    && categoryMeta
    && normalisationKind !== 'refund'
    && normalisationKind !== 'incoming-transfer'
    && normalisationKind !== 'internal-transfer'
    && normalisationKind !== 'loan-drawdown'
    && normalisationKind !== 'cash-withdrawal'
    && !transaction.needsReview,
  );

  return {
    transactionId: transaction.transactionId || null,
    accountId: transaction.accountId || null,
    bookingDate: transaction.bookingDate,
    monthKey: transaction.bookingDate.slice(0, 7),
    amount: roundCurrency(transaction.amount),
    absoluteAmount: roundCurrency(Math.abs(transaction.amount)),
    currency: transaction.currency || 'GBP',
    rawLabel,
    merchantKey,
    merchantLabel,
    patternKey,
    normalisationKind,
    categoryId,
    categoryLabel: transaction.categoryLabel || (categoryMeta ? `${categoryMeta.parentLabel}: ${categoryMeta.label}` : null),
    confidence,
    needsReview: Boolean(transaction.needsReview || reviewReason),
    reviewReason,
    budgetCategory: categoryMeta?.budgetCategory || null,
    countsTowardsIncome,
    countsTowardsSpend,
    summaryBucket: categoryMeta?.summaryBucket || null,
  };
}

function cadenceDays(cadence: BankRecurringCadence) {
  switch (cadence) {
    case 'weekly':
      return 7;
    case 'four-weekly':
      return 28;
    case 'monthly':
      return 30;
    case 'quarterly':
      return 91;
    case 'annual':
      return 365;
  }
}

function cadenceTolerance(cadence: BankRecurringCadence) {
  switch (cadence) {
    case 'weekly':
      return 2;
    case 'four-weekly':
      return 3;
    case 'monthly':
      return 5;
    case 'quarterly':
      return 10;
    case 'annual':
      return 20;
  }
}

function recurringMonthlyEquivalent(amount: number, cadence: BankRecurringCadence) {
  switch (cadence) {
    case 'weekly':
      return roundCurrency((amount * 52) / 12);
    case 'four-weekly':
      return roundCurrency((amount * 13) / 12);
    case 'monthly':
      return roundCurrency(amount);
    case 'quarterly':
      return roundCurrency(amount / 3);
    case 'annual':
      return roundCurrency(amount / 12);
  }
}

function detectRecurringCadence(entries: Array<{ bookingDate: string; amount: number }>): {
  cadence: BankRecurringCadence;
  confidence: MoneyConfidence;
} | null {
  if (entries.length < 2) return null;

  const sorted = [...entries].sort((left, right) => left.bookingDate.localeCompare(right.bookingDate));
  const intervals = sorted.slice(1).map((entry, index) => daysBetween(sorted[index].bookingDate, entry.bookingDate));
  const averageAmount = sorted.reduce((sum, entry) => sum + entry.amount, 0) / sorted.length;
  const amountTolerance = Math.max(8, averageAmount * 0.35);

  let best: { cadence: BankRecurringCadence; score: number } | null = null;
  for (const cadence of ['weekly', 'four-weekly', 'monthly', 'quarterly', 'annual'] as const) {
    const score = intervals.filter((value) => Math.abs(value - cadenceDays(cadence)) <= cadenceTolerance(cadence)).length;
    if (!best || score > best.score) {
      best = { cadence, score };
    }
  }

  if (!best || best.score <= 0) return null;
  const amountMatches = sorted.filter((entry) => Math.abs(entry.amount - averageAmount) <= amountTolerance).length;
  const requiredAmountMatches = Math.max(2, sorted.length - 1);
  if (amountMatches < requiredAmountMatches) return null;

  return {
    cadence: best.cadence,
    confidence: best.score >= Math.max(2, intervals.length) && amountMatches === sorted.length ? 'high' : 'medium',
  };
}

function isAnnualRenewalLikeCategory(categoryId: BankBudgetCategoryId) {
  return categoryId === 'insurance' || categoryId === 'travel_vehicle_costs' || categoryId === 'work_self_employed';
}

function isAnnualRenewalLikePattern(patternKey: string) {
  return ANNUAL_RENEWAL_HINTS.some((hint) => patternKey.includes(hint));
}

export function buildBankFeedMerchantReviewCandidates(
  transactions: readonly BankFeedTransactionInput[],
  options: BankFeedAnalysisOptions = {},
) {
  const ruleMaps = buildBankCategoryRuleMaps(options);
  const amountIndex = buildAmountMatchIndex(transactions);
  const merchantGroups = new Map<string, BankFeedMerchantReviewCandidate>();

  transactions
    .filter((entry) => Number.isFinite(entry.amount) && entry.amount < 0 && safeDate(entry.bookingDate))
    .forEach((transaction) => {
      const normalised = buildNormalisedTransaction(transaction, amountIndex, ruleMaps);
      if (!normalised.merchantKey || ruleMaps.merchantRules.has(normalised.merchantKey)) return;
      if (!normalised.needsReview || !normalised.categoryId) {
        return;
      }

      const reason: BankFeedMerchantReviewReason =
        normalised.reviewReason === 'broad-merchant'
          ? 'broad-merchant'
          : normalised.confidence === 'medium'
            ? 'medium-confidence'
            : 'low-confidence';
      const current = merchantGroups.get(normalised.merchantKey) || {
        merchantKey: normalised.merchantKey,
        merchantLabel: normalised.merchantLabel,
        suggestedCategory: normalised.budgetCategory || DEFAULT_EXPENSE_CATEGORY,
        confidence: normalised.confidence,
        reason,
        totalAmount: 0,
        averageAmount: 0,
        transactionCount: 0,
        latestBookingDate: transaction.bookingDate,
      } satisfies BankFeedMerchantReviewCandidate;

      current.totalAmount += Math.abs(transaction.amount);
      current.transactionCount += 1;
      current.averageAmount = roundCurrency(current.totalAmount / current.transactionCount);
      if (transaction.bookingDate > current.latestBookingDate) {
        current.latestBookingDate = transaction.bookingDate;
      }
      if (current.reason !== 'broad-merchant' && reason === 'broad-merchant') {
        current.reason = reason;
      } else if (current.reason === 'medium-confidence' && reason === 'low-confidence') {
        current.reason = reason;
      }
      if (current.confidence === 'medium' && normalised.confidence === 'low') {
        current.confidence = normalised.confidence;
      }
      merchantGroups.set(normalised.merchantKey, current);
    });

  return Array.from(merchantGroups.values())
    .sort((left, right) => {
      if (right.totalAmount !== left.totalAmount) {
        return right.totalAmount - left.totalAmount;
      }
      return right.transactionCount - left.transactionCount;
    });
}

export function buildBankFeedAnalysis(
  transactions: readonly BankFeedTransactionInput[],
  options: BankFeedAnalysisOptions = {},
): BankFeedAnalysis {
  const ruleMaps = buildBankCategoryRuleMaps(options);
  const sorted = [...transactions]
    .filter((entry) => Number.isFinite(entry.amount) && safeDate(entry.bookingDate))
    .sort((left, right) => left.bookingDate.localeCompare(right.bookingDate));
  const amountIndex = buildAmountMatchIndex(sorted);
  const normalisedTransactions = sorted.map((transaction) => buildNormalisedTransaction(transaction, amountIndex, ruleMaps));
  const coverageStart = sorted[0]?.bookingDate ?? null;
  const coverageEnd = sorted[sorted.length - 1]?.bookingDate ?? null;
  const monthsCovered = new Set(sorted.map((entry) => entry.bookingDate.slice(0, 7))).size;
  const coverageDays = daysBetween(coverageStart, coverageEnd);
  const primaryCurrency = sorted.find((entry) => entry.currency)?.currency || 'GBP';

  const outgoingCategoryTotals = Object.fromEntries(
    DEFAULT_BUDGET_CATEGORIES.map((category) => [category, { totalAmount: 0, transactionCount: 0 }]),
  ) as Record<CanonicalBudgetCategory, { totalAmount: number; transactionCount: number }>;
  const outgoingByMonth = new Map<
    string,
    Record<CanonicalBudgetCategory, { totalAmount: number; transactionCount: number }>
  >();
  const incomingSources = new Map<string, { source: string; totalAmount: number; transactionCount: number }>();
  const incomingMerchants = new Map<
    string,
    {
      source: string;
      categoryId: BankBudgetCategoryId;
      categoryLabel: string;
      normalisationKind: BankNormalisationKind;
      count: number;
      totalAmount: number;
      latestBookingDate: string;
      entries: Array<{ amount: number; bookingDate: string }>;
    }
  >();
  const outgoingMerchants = new Map<
    string,
    {
      merchant: string;
      category: CanonicalBudgetCategory;
      categoryId: BankBudgetCategoryId;
      categoryLabel: string;
      count: number;
      confidence: MoneyConfidence;
      totalAmount: number;
      latestBookingDate: string;
      samplePatternKey: string;
      entries: Array<{ amount: number; bookingDate: string; category: CanonicalBudgetCategory }>;
    }
  >();
  const broadMerchantNames = new Set<string>();

  let incomingTotal = 0;
  let outgoingTotal = 0;

  const breakdownTotals = new Map<BankBudgetCategoryId, {
    currentMonthAmount: number;
    currentMonthCount: number;
    annualTotal: number;
    annualCount: number;
  }>();
  const monthlyBucketByCategory = new Map<string, Map<BankBudgetCategoryId, number>>();
  const summaryMonthlyBuckets = new Map<string, Record<BankBudgetSummaryBucket, number>>();
  const reviewQueue: BankBudgetReviewItem[] = [];

  const addBreakdownAmount = (
    monthKey: string,
    categoryId: BankBudgetCategoryId,
    amount: number,
    isCurrentMonth: boolean,
  ) => {
    const current = breakdownTotals.get(categoryId) || {
      currentMonthAmount: 0,
      currentMonthCount: 0,
      annualTotal: 0,
      annualCount: 0,
    };
    current.annualTotal += amount;
    current.annualCount += 1;
    if (isCurrentMonth) {
      current.currentMonthAmount += amount;
      current.currentMonthCount += 1;
    }
    breakdownTotals.set(categoryId, current);

    const monthBucket = monthlyBucketByCategory.get(monthKey) || new Map<BankBudgetCategoryId, number>();
    monthBucket.set(categoryId, roundCurrency((monthBucket.get(categoryId) || 0) + amount));
    monthlyBucketByCategory.set(monthKey, monthBucket);
  };

  const latestMonthKey = normalisedTransactions.at(-1)?.monthKey || null;

  for (const transaction of normalisedTransactions) {
    const monthKey = transaction.monthKey;
    const budgetCategory = transaction.budgetCategory || DEFAULT_EXPENSE_CATEGORY;
    const isCurrentMonth = latestMonthKey === monthKey;

    const monthSummary = summaryMonthlyBuckets.get(monthKey) || {
      income: 0,
      essential: 0,
      debt: 0,
      savings: 0,
      discretionary: 0,
    } satisfies Record<BankBudgetSummaryBucket, number>;

    if (transaction.countsTowardsSpend) {
      const amount = transaction.absoluteAmount;
      outgoingTotal += amount;
      outgoingCategoryTotals[budgetCategory].totalAmount += amount;
      outgoingCategoryTotals[budgetCategory].transactionCount += 1;

      const monthBucket = outgoingByMonth.get(monthKey) || Object.fromEntries(
        DEFAULT_BUDGET_CATEGORIES.map((item) => [item, { totalAmount: 0, transactionCount: 0 }]),
      ) as Record<CanonicalBudgetCategory, { totalAmount: number; transactionCount: number }>;
      monthBucket[budgetCategory].totalAmount += amount;
      monthBucket[budgetCategory].transactionCount += 1;
      outgoingByMonth.set(monthKey, monthBucket);

      if (transaction.categoryId) {
        addBreakdownAmount(monthKey, transaction.categoryId, amount, isCurrentMonth);
      }

      if (transaction.summaryBucket) {
        monthSummary[transaction.summaryBucket] += amount;
      }

      if (transaction.merchantKey) {
        const current = outgoingMerchants.get(transaction.merchantKey) || {
          merchant: transaction.merchantLabel,
          category: budgetCategory,
          categoryId: transaction.categoryId || 'other_discretionary',
          categoryLabel: transaction.categoryLabel || 'Other discretionary',
          count: 0,
          confidence: transaction.confidence,
          totalAmount: 0,
          latestBookingDate: transaction.bookingDate,
          samplePatternKey: transaction.patternKey,
          entries: [],
        };
        current.count += 1;
        current.totalAmount += amount;
        current.latestBookingDate = transaction.bookingDate > current.latestBookingDate
          ? transaction.bookingDate
          : current.latestBookingDate;
        if (current.category === DEFAULT_EXPENSE_CATEGORY && budgetCategory !== DEFAULT_EXPENSE_CATEGORY) {
          current.category = budgetCategory;
        }
        if (current.confidence === 'medium' && transaction.confidence === 'high') {
          current.confidence = 'high';
        }
        if (current.samplePatternKey.length < transaction.patternKey.length) {
          current.samplePatternKey = transaction.patternKey;
        }
        current.entries.push({
          amount,
          bookingDate: transaction.bookingDate,
          category: budgetCategory,
        });
        outgoingMerchants.set(transaction.merchantKey, current);
      }

      if (BROAD_MERCHANT_HINTS.some((hint) => transaction.merchantKey.includes(hint))) {
        broadMerchantNames.add(transaction.merchantLabel);
      }
    } else if (transaction.countsTowardsIncome) {
      incomingTotal += transaction.absoluteAmount;
      const sourceKey = normaliseIncomeSourceKey(transaction.rawLabel) || transaction.merchantKey || 'income';
      const sourceLabel = sourceKey === 'income' ? 'Income' : getMoneyIncomeSourceDisplayName(transaction.rawLabel);
      const current = incomingSources.get(sourceKey) || {
        source: sourceLabel,
        totalAmount: 0,
        transactionCount: 0,
      };
      current.totalAmount += transaction.absoluteAmount;
      current.transactionCount += 1;
      incomingSources.set(sourceKey, current);
      const recurringIncomeCurrent = incomingMerchants.get(sourceKey) || {
        source: sourceLabel,
        categoryId: transaction.categoryId || 'income_other',
        categoryLabel: transaction.categoryLabel || 'Income: Other income',
        normalisationKind: transaction.normalisationKind,
        count: 0,
        totalAmount: 0,
        latestBookingDate: transaction.bookingDate,
        entries: [],
      };
      recurringIncomeCurrent.count += 1;
      recurringIncomeCurrent.totalAmount += transaction.absoluteAmount;
      recurringIncomeCurrent.latestBookingDate = transaction.bookingDate > recurringIncomeCurrent.latestBookingDate
        ? transaction.bookingDate
        : recurringIncomeCurrent.latestBookingDate;
      if (recurringIncomeCurrent.normalisationKind === 'income' && transaction.normalisationKind !== 'income') {
        recurringIncomeCurrent.normalisationKind = transaction.normalisationKind;
      }
      recurringIncomeCurrent.entries.push({
        amount: transaction.absoluteAmount,
        bookingDate: transaction.bookingDate,
      });
      incomingMerchants.set(sourceKey, recurringIncomeCurrent);
      if (transaction.categoryId) {
        addBreakdownAmount(monthKey, transaction.categoryId, transaction.absoluteAmount, isCurrentMonth);
      }
      monthSummary.income += transaction.absoluteAmount;
    }

    if (transaction.needsReview) {
      reviewQueue.push({
        transactionId: transaction.transactionId,
        bookingDate: transaction.bookingDate,
        amount: transaction.absoluteAmount,
        currency: transaction.currency,
        merchantKey: transaction.merchantKey,
        merchantLabel: transaction.merchantLabel,
        patternKey: transaction.patternKey,
        rawLabel: transaction.rawLabel,
        suggestedCategoryId: transaction.categoryId,
        suggestedCategoryLabel: transaction.categoryLabel,
        confidence: transaction.confidence,
        reason: transaction.reviewReason || 'low-confidence',
      });
    }

    summaryMonthlyBuckets.set(monthKey, monthSummary);
  }

  const latestMonthBucket = latestMonthKey ? outgoingByMonth.get(latestMonthKey) || null : null;
  const currentMonthSpendingByCategory = latestMonthBucket
    ? DEFAULT_BUDGET_CATEGORIES
      .map((category) => ({
        category,
        totalAmount: roundCurrency(latestMonthBucket[category].totalAmount),
        transactionCount: latestMonthBucket[category].transactionCount,
      }))
      .filter((entry) => entry.totalAmount > 0)
      .sort((left, right) => right.totalAmount - left.totalAmount)
    : [];
  const monthlyAverageByCategory = DEFAULT_BUDGET_CATEGORIES
    .map((category) => ({
      category,
      averageAmount: roundCurrency(monthsCovered > 0 ? outgoingCategoryTotals[category].totalAmount / monthsCovered : 0),
    }))
    .filter((entry) => entry.averageAmount > 0);

  const suggestedBudget = Object.fromEntries(
    DEFAULT_BUDGET_CATEGORIES.map((category) => {
      const monthlyAverage = monthsCovered > 0 ? outgoingCategoryTotals[category].totalAmount / monthsCovered : 0;
      return [category, roundBudgetLimit(monthlyAverage * 1.1)];
    }),
  ) as Record<CanonicalBudgetCategory, number>;

  const previousYearMonthKey = latestMonthKey
    ? `${String(Number(latestMonthKey.slice(0, 4)) - 1)}-${latestMonthKey.slice(5, 7)}`
    : null;
  const previousYearBucket = previousYearMonthKey ? outgoingByMonth.get(previousYearMonthKey) || null : null;
  const yearOnYear =
    latestMonthKey && latestMonthBucket && previousYearMonthKey && previousYearBucket
      ? {
          currentMonthKey: latestMonthKey,
          previousYearMonthKey,
          currentOutgoingTotal: roundCurrency(
            DEFAULT_BUDGET_CATEGORIES.reduce((sum, category) => sum + latestMonthBucket[category].totalAmount, 0),
          ),
          previousOutgoingTotal: roundCurrency(
            DEFAULT_BUDGET_CATEGORIES.reduce((sum, category) => sum + previousYearBucket[category].totalAmount, 0),
          ),
          differenceAmount: roundCurrency(
            DEFAULT_BUDGET_CATEGORIES.reduce(
              (sum, category) => sum + latestMonthBucket[category].totalAmount - previousYearBucket[category].totalAmount,
              0,
            ),
          ),
          categoryChanges: DEFAULT_BUDGET_CATEGORIES
            .map((category) => ({
              category,
              currentAmount: roundCurrency(latestMonthBucket[category].totalAmount),
              previousAmount: roundCurrency(previousYearBucket[category].totalAmount),
              differenceAmount: roundCurrency(latestMonthBucket[category].totalAmount - previousYearBucket[category].totalAmount),
            }))
            .filter((entry) => entry.currentAmount > 0 || entry.previousAmount > 0)
            .sort((left, right) => Math.abs(right.differenceAmount) - Math.abs(left.differenceAmount))
            .slice(0, 3),
        }
      : null;

  const unusualSpendFlags = Array.from(outgoingMerchants.values())
    .flatMap((merchant) => {
      if (merchant.entries.length < 3 || !coverageEnd) {
        return [];
      }

      const recentCutoff = new Date(coverageEnd);
      recentCutoff.setDate(recentCutoff.getDate() - 90);
      const recentCutoffKey = recentCutoff.toISOString().slice(0, 10);

      return merchant.entries
        .filter((entry) => entry.bookingDate >= recentCutoffKey)
        .map((entry) => {
          const baselineCount = merchant.entries.length - 1;
          if (baselineCount < 2) return null;

          const baselineAverage = roundCurrency((merchant.totalAmount - entry.amount) / baselineCount);
          if (baselineAverage <= 0) return null;
          if (entry.amount < baselineAverage * 1.75) return null;
          if (entry.amount - baselineAverage < 20) return null;

          return {
            merchant: merchant.merchant,
            category: merchant.category,
            amount: roundCurrency(entry.amount),
            typicalAmount: baselineAverage,
            bookingDate: entry.bookingDate,
            message: `${merchant.merchant} was about ${roundCurrency(entry.amount - baselineAverage).toFixed(0)} higher than your usual spend there.`,
          } satisfies BankFeedUnusualSpendFlag;
        })
        .filter((entry): entry is BankFeedUnusualSpendFlag => Boolean(entry));
    })
    .sort((left, right) => (right.amount - right.typicalAmount) - (left.amount - left.typicalAmount))
    .slice(0, 3);

  const overspendSignals = currentMonthSpendingByCategory
    .map((entry) => {
      const monthlyAverage = monthlyAverageByCategory.find((item) => item.category === entry.category)?.averageAmount || 0;
      const differenceAmount = roundCurrency(entry.totalAmount - monthlyAverage);

      if (monthlyAverage < 40 || differenceAmount < 30 || entry.totalAmount < monthlyAverage * 1.2) {
        return null;
      }

      return {
        category: entry.category,
        currentAmount: entry.totalAmount,
        averageAmount: monthlyAverage,
        differenceAmount,
        message: `${entry.category} is already about £${differenceAmount.toFixed(0)} above your usual monthly bank pattern.`,
      } satisfies BankFeedOverspendSignal;
    })
    .filter((entry): entry is BankFeedOverspendSignal => Boolean(entry))
    .sort((left, right) => right.differenceAmount - left.differenceAmount)
    .slice(0, 3);

  const recurringOutgoings: BankFeedRecurringMerchant[] = Array.from(outgoingMerchants.values())
    .map((merchant) => {
      const recurring = detectRecurringCadence(
        merchant.entries.map((entry) => ({ bookingDate: entry.bookingDate, amount: entry.amount })),
      );
      if (!recurring) return null;
      return {
        merchant: merchant.merchant,
        category: merchant.category,
        categoryId: merchant.categoryId,
        categoryLabel: merchant.categoryLabel,
        count: merchant.count,
        cadence: recurring.cadence,
        confidence: recurring.confidence,
        totalAmount: roundCurrency(merchant.totalAmount),
        averageAmount: roundCurrency(merchant.totalAmount / merchant.count),
        monthlyEquivalent: recurringMonthlyEquivalent(merchant.totalAmount / merchant.count, recurring.cadence),
        latestBookingDate: merchant.latestBookingDate,
      } satisfies BankFeedRecurringMerchant;
    })
    .filter((entry): entry is BankFeedRecurringMerchant => Boolean(entry))
    .sort((left, right) => right.monthlyEquivalent - left.monthlyEquivalent)
    .slice(0, 16);

  const recurringIncomes: BankFeedRecurringIncome[] = Array.from(incomingMerchants.values())
    .map((source) => {
      const recurring = detectRecurringCadence(
        source.entries.map((entry) => ({ bookingDate: entry.bookingDate, amount: entry.amount })),
      );
      if (!recurring) return null;
      return {
        source: source.source,
        categoryId: source.categoryId,
        categoryLabel: source.categoryLabel,
        count: source.count,
        cadence: recurring.cadence,
        confidence: recurring.confidence,
        totalAmount: roundCurrency(source.totalAmount),
        averageAmount: roundCurrency(source.totalAmount / source.count),
        monthlyEquivalent: recurringMonthlyEquivalent(source.totalAmount / source.count, recurring.cadence),
        latestBookingDate: source.latestBookingDate,
        normalisationKind: source.normalisationKind,
      } satisfies BankFeedRecurringIncome;
    })
    .filter((entry): entry is BankFeedRecurringIncome => Boolean(entry))
    .sort((left, right) => right.monthlyEquivalent - left.monthlyEquivalent)
    .slice(0, 12);

  const annualRenewalCandidates: BankFeedAnnualRenewalCandidate[] = Array.from(outgoingMerchants.values())
    .map((merchant) => {
      const recurring = detectRecurringCadence(
        merchant.entries.map((entry) => ({ bookingDate: entry.bookingDate, amount: entry.amount })),
      );
      if (recurring?.cadence === 'annual') {
        return {
          merchant: merchant.merchant,
          category: merchant.category,
          categoryId: merchant.categoryId,
          categoryLabel: merchant.categoryLabel,
          confidence: recurring.confidence,
          annualAmount: roundCurrency(merchant.totalAmount / merchant.count),
          latestBookingDate: merchant.latestBookingDate,
          reason: 'Repeated about yearly in the imported history.',
        } satisfies BankFeedAnnualRenewalCandidate;
      }

      const annualHint = isAnnualRenewalLikePattern(merchant.samplePatternKey) || isAnnualRenewalLikeCategory(merchant.categoryId);
      if (!annualHint) return null;
      if (merchant.count > 2) return null;
      if (merchant.totalAmount < 20) return null;

      return {
        merchant: merchant.merchant,
        category: merchant.category,
        categoryId: merchant.categoryId,
        categoryLabel: merchant.categoryLabel,
        confidence: merchant.count > 1 ? 'medium' : 'low',
        annualAmount: roundCurrency(merchant.totalAmount / merchant.count),
        latestBookingDate: merchant.latestBookingDate,
        reason: 'Looks provider-linked or renewal-like, even though only a short bank history is visible.',
      } satisfies BankFeedAnnualRenewalCandidate;
    })
    .filter((entry): entry is BankFeedAnnualRenewalCandidate => Boolean(entry))
    .sort((left, right) => right.annualAmount - left.annualAmount)
    .slice(0, 8);

  const buildMetric = (bucket: BankBudgetSummaryBucket | 'disposable' | 'leftover-essentials' | 'leftover-essentials-savings'): BankDisposableMetric => {
    const currentMonth = latestMonthKey ? (summaryMonthlyBuckets.get(latestMonthKey) || {
      income: 0,
      essential: 0,
      debt: 0,
      savings: 0,
      discretionary: 0,
    }) : {
      income: 0,
      essential: 0,
      debt: 0,
      savings: 0,
      discretionary: 0,
    };
    const monthEntries = Array.from(summaryMonthlyBuckets.values());
    const monthlyAverage = monthEntries.length
      ? roundCurrency(
        monthEntries.reduce((sum, entry) => {
          if (bucket === 'disposable') return sum + (entry.income - entry.essential - entry.debt);
          if (bucket === 'leftover-essentials') return sum + (entry.income - entry.essential);
          if (bucket === 'leftover-essentials-savings') return sum + (entry.income - entry.essential - entry.savings);
          return sum + entry[bucket];
        }, 0) / monthEntries.length,
      )
      : 0;
    const annualTotal = roundCurrency(
      monthEntries.reduce((sum, entry) => {
        if (bucket === 'disposable') return sum + (entry.income - entry.essential - entry.debt);
        if (bucket === 'leftover-essentials') return sum + (entry.income - entry.essential);
        if (bucket === 'leftover-essentials-savings') return sum + (entry.income - entry.essential - entry.savings);
        return sum + entry[bucket];
      }, 0),
    );

    const monthlyActual =
      bucket === 'disposable'
        ? roundCurrency(currentMonth.income - currentMonth.essential - currentMonth.debt)
        : bucket === 'leftover-essentials'
          ? roundCurrency(currentMonth.income - currentMonth.essential)
          : bucket === 'leftover-essentials-savings'
            ? roundCurrency(currentMonth.income - currentMonth.essential - currentMonth.savings)
            : roundCurrency(currentMonth[bucket]);

    return {
      monthlyActual,
      rollingMonthlyAverage: monthlyAverage,
      annualTotal,
      annualisedMonthlyEquivalent: roundCurrency(monthEntries.length ? annualTotal / monthEntries.length : 0),
    };
  };

  const budgetBreakdownRows = BANK_BREAKDOWN_CATEGORY_DEFINITIONS
    .flatMap((definition): BankBudgetCategoryRow[] => {
      const totals = breakdownTotals.get(definition.id);
      if (!totals) return [];
      const monthlyAverage = monthsCovered > 0
        ? roundCurrency(totals.annualTotal / monthsCovered)
        : 0;
      return [{
        categoryId: definition.id,
        categoryLabel: definition.label,
        parentLabel: definition.parentLabel,
        section: definition.section,
        summaryBucket: definition.summaryBucket,
        currentMonthAmount: roundCurrency(totals.currentMonthAmount),
        currentMonthCount: totals.currentMonthCount,
        rollingMonthlyAverage: monthlyAverage,
        annualTotal: roundCurrency(totals.annualTotal),
        annualCount: totals.annualCount,
        annualisedMonthlyEquivalent: roundCurrency(monthlyAverage),
      }];
    })
    .sort((left, right) => right.annualisedMonthlyEquivalent - left.annualisedMonthlyEquivalent);

  return {
    transactionCount: sorted.length,
    coverageStart,
    coverageEnd,
    coverageDays,
    monthsCovered,
    likelyPartialYear: Boolean(sorted.length) && coverageDays < 330,
    latestMonthKey,
    primaryCurrency,
    incomingTotal: roundCurrency(incomingTotal),
    outgoingTotal: roundCurrency(outgoingTotal),
    netTotal: roundCurrency(incomingTotal - outgoingTotal),
    spendingByCategory: DEFAULT_BUDGET_CATEGORIES
      .map((category) => ({
        category,
        totalAmount: roundCurrency(outgoingCategoryTotals[category].totalAmount),
        transactionCount: outgoingCategoryTotals[category].transactionCount,
      }))
      .filter((entry) => entry.totalAmount > 0)
      .sort((left, right) => right.totalAmount - left.totalAmount),
    currentMonthSpendingByCategory,
    monthlyAverageByCategory,
    suggestedBudget,
    incomeBySource: Array.from(incomingSources.values())
      .map((entry) => ({
        source: entry.source,
        totalAmount: roundCurrency(entry.totalAmount),
        transactionCount: entry.transactionCount,
      }))
      .sort((left, right) => right.totalAmount - left.totalAmount)
      .slice(0, 5),
    recurringIncomes,
    recurringOutgoings,
    annualRenewalCandidates,
    budgetBreakdown: {
      currentMonthKey: latestMonthKey,
      rows: budgetBreakdownRows,
      disposableIncome: {
        income: buildMetric('income'),
        essentialHouseholdCosts: buildMetric('essential'),
        committedDebtRepayments: buildMetric('debt'),
        savingsContributions: buildMetric('savings'),
        discretionarySpend: buildMetric('discretionary'),
        disposableIncome: buildMetric('disposable'),
        leftoverAfterEssentialsOnly: buildMetric('leftover-essentials'),
        leftoverAfterEssentialsAndSavings: buildMetric('leftover-essentials-savings'),
      },
      recurringCommitments: recurringOutgoings,
      reviewQueue: reviewQueue
        .sort((left, right) => right.amount - left.amount)
        .slice(0, 12),
    },
    normalisedTransactions,
    yearOnYear,
    unusualSpendFlags,
    overspendSignals,
    broadMerchantNames: Array.from(broadMerchantNames).slice(0, 4),
  };
}

export function resolveMoneyHouseholdBenchmark(input: MoneyHouseholdBenchmarkInput): MoneyHouseholdBenchmark | null {
  const hasPartner = Boolean(input.partnerName?.trim()) || Boolean(input.hasHouseholdShare);
  const childrenCount = Math.max(0, Math.round(input.childrenCount || 0));

  let key: MoneyHouseholdBenchmarkKey = hasPartner ? 'two_adults' : 'one_adult';
  let exactMatch = true;
  let note: string | null = null;

  if (hasPartner) {
    if (childrenCount === 1) {
      key = 'two_adults_one_child';
    } else if (childrenCount === 2) {
      key = 'two_adults_two_children';
    } else if (childrenCount >= 3) {
      key = 'two_adults_three_children';
      if (childrenCount > 3) {
        exactMatch = false;
        note = 'Adulting is using the closest published ONS household mix for 3 or more children.';
      }
    }
  } else if (childrenCount > 0) {
    key = 'one_adult';
    exactMatch = false;
    note = 'Adulting is using the closest published ONS one-adult benchmark because this pack does not include a single-adult-with-children cut.';
  }

  const source = MONEY_HOUSEHOLD_WEEKLY_BENCHMARKS[key];
  return {
    key,
    householdLabel: source.householdLabel,
    sourceLabel: MONEY_HOUSEHOLD_BENCHMARK_SOURCE,
    exactMatch,
    note,
    monthlyByCategory: Object.fromEntries(
      Object.entries(source.weeklyByCategory).map(([category, value]) => [category, monthlyBudgetFromWeekly(value)]),
    ) as Partial<Record<CanonicalBudgetCategory, number>>,
  };
}

export function buildMoneyBudgetBenchmarkRows(
  suggestedBudget: Partial<Record<CanonicalBudgetCategory, number>>,
  householdInput: MoneyHouseholdBenchmarkInput,
): {
  benchmark: MoneyHouseholdBenchmark | null;
  rows: MoneyBudgetBenchmarkComparisonRow[];
} {
  const benchmark = resolveMoneyHouseholdBenchmark(householdInput);
  if (!benchmark) {
    return { benchmark: null, rows: [] };
  }

  const rows = DEFAULT_BUDGET_CATEGORIES
    .map((category) => {
      const suggestedLimit = suggestedBudget[category] || 0;
      const benchmarkMonthly = benchmark.monthlyByCategory[category] || 0;
      if (suggestedLimit <= 0 || benchmarkMonthly <= 0) return null;

      return {
        category,
        suggestedLimit,
        benchmarkMonthly,
        differenceAmount: roundCurrency(suggestedLimit - benchmarkMonthly),
      } satisfies MoneyBudgetBenchmarkComparisonRow;
    })
    .filter((entry): entry is MoneyBudgetBenchmarkComparisonRow => Boolean(entry))
    .sort((left, right) => Math.abs(right.differenceAmount) - Math.abs(left.differenceAmount));

  return { benchmark, rows };
}

export function buildBankFeedBudgetSignals(
  transactions: readonly BankFeedTransactionInput[],
  currentBudgetLimits: ReadonlyArray<{ category: string; limit: number }>,
  householdInput: MoneyHouseholdBenchmarkInput = {},
  options: BankFeedAnalysisOptions = {},
): BankFeedBudgetSignals {
  const analysis = buildBankFeedAnalysis(transactions, options);
  const { benchmark, rows: benchmarkRows } = buildMoneyBudgetBenchmarkRows(analysis.suggestedBudget, householdInput);
  const budgetLimitByCategory = new Map(
    currentBudgetLimits.map((entry) => [normaliseBudgetCategoryLabel(entry.category), entry.limit]),
  );
  const benchmarkByCategory = new Map(benchmarkRows.map((entry) => [entry.category, entry]));

  const suggestedBudgetRows = DEFAULT_BUDGET_CATEGORIES
    .map((category) => {
      const currentLimit = budgetLimitByCategory.has(category)
        ? Number(budgetLimitByCategory.get(category) || 0)
        : null;
      const currentMonthSpend =
        analysis.currentMonthSpendingByCategory.find((entry) => entry.category === category)?.totalAmount || 0;
      const monthlyAverage =
        analysis.monthlyAverageByCategory.find((entry) => entry.category === category)?.averageAmount || 0;
      const suggestedLimit = analysis.suggestedBudget[category] || 0;
      const benchmarkRow = benchmarkByCategory.get(category);

      if (
        suggestedLimit <= 0
        && currentLimit == null
        && currentMonthSpend <= 0
        && !benchmarkRow?.benchmarkMonthly
      ) {
        return null;
      }

      return {
        category,
        currentLimit,
        currentMonthSpend,
        monthlyAverage,
        suggestedLimit,
        benchmarkMonthly: benchmarkRow?.benchmarkMonthly || null,
        benchmarkDifference: benchmarkRow ? benchmarkRow.differenceAmount : null,
      } satisfies BankFeedBudgetSuggestionRow;
    })
    .filter((entry): entry is BankFeedBudgetSuggestionRow => Boolean(entry))
    .sort(
      (left, right) =>
        Math.max(right.suggestedLimit, right.currentMonthSpend, right.currentLimit || 0)
        - Math.max(left.suggestedLimit, left.currentMonthSpend, left.currentLimit || 0),
    );

  const overBudgetCategories = suggestedBudgetRows
    .filter((entry) => entry.currentLimit != null && entry.currentLimit > 0 && entry.currentMonthSpend > entry.currentLimit)
    .map((entry) => ({
      category: entry.category,
      spentAmount: entry.currentMonthSpend,
      budgetLimit: entry.currentLimit || 0,
      overBy: roundCurrency(entry.currentMonthSpend - (entry.currentLimit || 0)),
    }));

  const unusualSpendFlags = Array.from(
    new Set([
      ...analysis.overspendSignals.map((entry) => entry.message),
      ...analysis.unusualSpendFlags.map(
        (entry) =>
          `${entry.merchant} is averaging about £${roundCurrency(entry.typicalAmount).toFixed(0)}, but the latest outgoing was about £${roundCurrency(entry.amount).toFixed(0)}.`,
      ),
    ]),
  );

  return {
    benchmark,
    suggestedBudgetRows,
    overBudgetCategories,
    unusualSpendFlags,
  };
}

export function buildBankFinanceAnalysisViews(
  analysis: BankFeedAnalysis,
  context: BankFinanceAnalysisContext = {},
): BankFinanceAnalysisViews {
  const householdLabel = buildFinanceHouseholdLabel(context);
  const priorityCosts = analysis.budgetBreakdown.disposableIncome.essentialHouseholdCosts;
  const protectedSavingsBuffer = analysis.budgetBreakdown.disposableIncome.savingsContributions;
  const currentDebtRepayments = analysis.budgetBreakdown.disposableIncome.committedDebtRepayments;
  const priorityCostBalance = analysis.budgetBreakdown.disposableIncome.leftoverAfterEssentialsOnly;
  const availableIncomeForCreditors = analysis.budgetBreakdown.disposableIncome.leftoverAfterEssentialsAndSavings;

  const committedNonDebtCosts = sumBudgetBreakdownRows(
    analysis.budgetBreakdown.rows,
    (row) => row.section === 'housing' || row.section === 'childcare-school' || row.section === 'insurance',
  );
  const committedMonthlyOutgoings = addDisposableMetrics(committedNonDebtCosts, currentDebtRepayments);
  const otherCoreLivingCosts = subtractDisposableMetrics(priorityCosts, committedNonDebtCosts);
  const headroomAfterCommitted = subtractDisposableMetrics(
    analysis.budgetBreakdown.disposableIncome.income,
    committedMonthlyOutgoings,
  );
  const headroomAfterCoreLivingCosts = subtractDisposableMetrics(
    headroomAfterCommitted,
    otherCoreLivingCosts,
  );

  const creditorView: BankCreditorAnalysisView = {
    householdLabel,
    priorityCosts,
    protectedSavingsBuffer,
    currentDebtRepayments,
    priorityCostBalance,
    availableIncomeForCreditors,
    guidance:
      availableIncomeForCreditors.rollingMonthlyAverage < 0
        ? 'Essentials and the current savings buffer are still higher than the incoming money Adulting can see, so this is a support-and-stabilise picture before it is a creditor-offer picture.'
        : availableIncomeForCreditors.rollingMonthlyAverage < 75
          ? 'There is only a small monthly amount left once essentials and the current savings buffer are protected, so keep offers realistic and review uncertain transactions first.'
          : 'Adulting can now show a calmer starting figure for affordable repayment offers after essentials and the current savings buffer are protected.',
  };

  const annualIncomeOnProfile = context.annualIncome && context.annualIncome > 0
    ? roundCurrency(context.annualIncome)
    : null;
  const declaredMonthlyIncomeOnProfile = annualIncomeOnProfile != null
    ? roundCurrency(annualIncomeOnProfile / 12)
    : null;
  const mortgagePrepView: BankMortgagePrepView = {
    householdLabel,
    annualIncomeOnProfile,
    declaredMonthlyIncomeOnProfile,
    netMonthlyIncomeFromBank: analysis.budgetBreakdown.disposableIncome.income,
    committedMonthlyOutgoings,
    otherCoreLivingCosts,
    headroomAfterCommitted,
    headroomAfterCoreLivingCosts,
    guidance:
      annualIncomeOnProfile == null
        ? 'Save annual income in Money & finance before using this as a mortgage-prep pack, because lenders usually ask for declared annual income as well as monthly commitments.'
        : 'This is a lender-style prep snapshot built from the same household budget. It is useful for gathering figures, but it is not a lender decision.',
  };

  return {
    creditorView,
    mortgagePrepView,
  };
}

export function buildBankCreditorSummaryText(
  view: BankCreditorAnalysisView,
  currency = 'GBP',
) {
  return [
    'Adulting creditor-ready summary',
    `Household shape: ${view.householdLabel}`,
    `Priority costs per month: ${formatFinanceCurrencyText(view.priorityCosts.rollingMonthlyAverage, currency)}`,
    `Protected savings buffer per month: ${formatFinanceCurrencyText(view.protectedSavingsBuffer.rollingMonthlyAverage, currency)}`,
    `Current debt repayments per month: ${formatFinanceCurrencyText(view.currentDebtRepayments.rollingMonthlyAverage, currency)}`,
    `Priority-cost balance per month: ${formatFinanceCurrencyText(view.priorityCostBalance.rollingMonthlyAverage, currency)}`,
    `Available income for creditors per month: ${formatFinanceCurrencyText(view.availableIncomeForCreditors.rollingMonthlyAverage, currency)}`,
    '',
    view.guidance,
  ].join('\n');
}

export function buildBankMortgagePrepSummaryText(
  view: BankMortgagePrepView,
  currency = 'GBP',
) {
  return [
    'Adulting mortgage-prep summary',
    `Household shape: ${view.householdLabel}`,
    `Annual income saved on profile: ${formatFinanceCurrencyText(view.annualIncomeOnProfile, currency)}`,
    `Declared monthly income from profile: ${formatFinanceCurrencyText(view.declaredMonthlyIncomeOnProfile, currency)}`,
    `Net monthly income from bank history: ${formatFinanceCurrencyText(view.netMonthlyIncomeFromBank.rollingMonthlyAverage, currency)}`,
    `Committed monthly outgoings: ${formatFinanceCurrencyText(view.committedMonthlyOutgoings.rollingMonthlyAverage, currency)}`,
    `Other core living costs per month: ${formatFinanceCurrencyText(view.otherCoreLivingCosts.rollingMonthlyAverage, currency)}`,
    `Headroom after committed outgoings: ${formatFinanceCurrencyText(view.headroomAfterCommitted.rollingMonthlyAverage, currency)}`,
    `Headroom after core living costs: ${formatFinanceCurrencyText(view.headroomAfterCoreLivingCosts.rollingMonthlyAverage, currency)}`,
    '',
    view.guidance,
  ].join('\n');
}

export function mergeSuggestedBudgetLimits(
  currentBudgetLimits: ReadonlyArray<{ category: string; limit: number }>,
  suggestedBudgetRows: readonly BankFeedBudgetSuggestionRow[],
) {
  const nextLimits = new Map<string, number>();

  for (const entry of currentBudgetLimits) {
    nextLimits.set(normaliseBudgetCategoryLabel(entry.category), entry.limit);
  }

  for (const entry of suggestedBudgetRows) {
    nextLimits.set(entry.category, entry.suggestedLimit);
  }

  const orderedCategories = buildBudgetCategoryOptions(
    DEFAULT_BUDGET_CATEGORIES,
    currentBudgetLimits.map((entry) => entry.category),
    suggestedBudgetRows.map((entry) => entry.category),
  );

  return orderedCategories.map((category) => ({
    category,
    limit: roundCurrency(nextLimits.get(category) || 0),
  }));
}

export function shouldAutoRefreshConnectedBankBudget({
  enabled,
  latestBankSyncAt,
  lastAppliedSyncAt,
  suggestedBudgetRowCount,
}: ConnectedBankBudgetAutoRefreshInput) {
  if (!enabled || !latestBankSyncAt || suggestedBudgetRowCount <= 0) {
    return false;
  }

  const latestSyncTime = Date.parse(latestBankSyncAt);
  if (Number.isNaN(latestSyncTime)) {
    return false;
  }

  if (!lastAppliedSyncAt) {
    return true;
  }

  const lastAppliedTime = Date.parse(lastAppliedSyncAt);
  if (Number.isNaN(lastAppliedTime)) {
    return true;
  }

  return latestSyncTime > lastAppliedTime;
}

export function normaliseBudgetCategoryLabel(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

export function buildBudgetCategoryOptions(...groups: Array<readonly string[] | null | undefined>) {
  const seen = new Set<string>();
  const options: string[] = [];

  for (const group of groups) {
    if (!group) continue;

    for (const item of group) {
      const label = normaliseBudgetCategoryLabel(item);
      if (!label || seen.has(label)) {
        continue;
      }

      seen.add(label);
      options.push(label);
    }
  }

  return options;
}

export type BudgetCategoryPreference = {
  id: string;
  label: string;
  hidden: boolean;
};

export type BudgetCategoryCollections = {
  all: string[];
  visible: string[];
  hidden: string[];
};

function sanitiseStorageSegment(value: string, fallback: string) {
  const cleaned = value
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return cleaned || fallback;
}

export function isDefaultBudgetCategory(value: string) {
  return DEFAULT_BUDGET_CATEGORIES.includes(value as (typeof DEFAULT_BUDGET_CATEGORIES)[number]);
}

export function buildBudgetCategoryCollections(input: {
  budgetLimitCategories?: readonly string[] | null;
  expenseCategories?: readonly string[] | null;
  preferences?: readonly BudgetCategoryPreference[] | null;
}) {
  const preferences = input.preferences || [];
  const all = buildBudgetCategoryOptions(
    DEFAULT_BUDGET_CATEGORIES,
    input.budgetLimitCategories,
    input.expenseCategories,
    preferences.map((item) => item.label),
  );
  const hiddenSet = new Set(
    preferences
      .filter((item) => item.hidden)
      .map((item) => normaliseBudgetCategoryLabel(item.label))
      .filter(Boolean),
  );

  return {
    all,
    visible: all.filter((item) => !hiddenSet.has(item)),
    hidden: all.filter((item) => hiddenSet.has(item)),
  } satisfies BudgetCategoryCollections;
}

export function hasBudgetCustomisation(
  budgetLimits: ReadonlyArray<{ category: string }> | null | undefined,
  annualIncome: number | null | undefined,
  budgetCategoryPreferenceCount: number | null | undefined,
) {
  return Boolean(
    (budgetLimits?.length || 0) > 0
    || Number(annualIncome || 0) > 0
    || Number(budgetCategoryPreferenceCount || 0) > 0,
  );
}

export function buildExpenseReceiptStoragePath(userId: string, expenseId: string, fileName: string) {
  const safeName = sanitiseStorageSegment(fileName, 'receipt');
  return `${userId}/money-receipts/${expenseId}/${Date.now()}_${safeName}`;
}
